// Member Signup Checkbox Web Component
var MemberSignupCheckbox = class extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="checkbox"].checkbox');

    if (this.input) {
      this.input.addEventListener('change', this._onCheckboxChange.bind(this));
    }

    // Prevent accordion toggle when clicking on checkbox area
    this.addEventListener('click', this._preventAccordionToggle.bind(this));
  }

  _preventAccordionToggle(event) {
    // Stop event from bubbling up to the summary element
    event.stopPropagation();
  }

  async _onCheckboxChange(event) {
    const isChecked = event.target.checked;
    const attributeValue = isChecked ? 'true' : '';

    try {
      // Update cart attributes via Shopify Cart API
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            new_member: attributeValue
          }
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const cartContent = await response.json();

      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
      }));

      // For main cart page, also reload the page to show updated member pricing
      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
      }

    } catch (error) {
      console.error('Error updating member signup:', error);
      // Revert checkbox state on error
      event.target.checked = !isChecked;
    }
  }
};

// Tax Toggle Web Component
var TaxToggle = class extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector('input[type="checkbox"]');

    if (this.toggle) {
      this.toggle.addEventListener('change', this._onToggleChange.bind(this));
    }
  }

  async _onToggleChange(event) {
    const isChecked = event.target.checked;
    const isInverted = this.hasAttribute('data-inverted');
    const taxesExcluded = isInverted ? !isChecked : isChecked;
    const attributeValue = taxesExcluded ? 'true' : '';

    try {
      // Update cart attributes via Shopify Cart API
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            taxes_excluded: attributeValue
          }
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      // Reload page to reflect tax changes in Liquid rendering
      window.location.reload();

    } catch (error) {
      console.error('Error updating tax toggle:', error);
      // Revert toggle state on error
      event.target.checked = !isChecked;
    }
  }
};

// Customer Type Selector Web Component
class CustomerTypeSelector extends HTMLElement {
  constructor() {
    super();
    this.context = this.getAttribute('data-context') || 'cart';
    this.selectedType = null;
    this.modal = null;
  }

  connectedCallback() {
    // DOM is ready, now we can initialize
    this.init();
  }

  init() {
    this.radioButtons = this.querySelectorAll('input[type="radio"]');
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.checkoutButtons = this.getCheckoutButtons();

    if (this.checkbox) {
      this.initCheckboxMode();
      return;
    }

    if (this.radioButtons.length === 0) {
      this.enableCheckout();
      return;
    }

    // Determine initial state
    if (this.hasB2BSignupCompleted()) {
      const b2bRadio = this.querySelector('input[value="b2b"]');
      if (b2bRadio) {
        b2bRadio.checked = true;
        this.selectedType = 'b2b';
      }
    } else {
      const checkedRadio = this.querySelector('input[type="radio"]:checked');
      if (checkedRadio) {
        this.selectedType = checkedRadio.value;
      } else if (this.hasPrivateSelectionStored()) {
        const privateRadio = this.querySelector('input[value="private"]');
        if (privateRadio) {
          privateRadio.checked = true;
          this.selectedType = 'private';
        }
      }
    }

    this.radioButtons.forEach(radio => {
      radio.addEventListener('change', this.handleSelectionChange.bind(this));
    });

    this.interceptCheckoutButtons();

    if (this.selectedType) {
      this.enableCheckout();
    } else {
      this.disableCheckout();
    }
  }

  initCheckboxMode() {
    if (this.hasB2BSignupCompleted()) {
      this.checkbox.checked = true;
      this.selectedType = 'b2b';
    } else {
      this.selectedType = 'private';
    }

    this.checkbox.addEventListener('change', () => {
      if (this.checkbox.checked) {
        this.selectedType = 'b2b';
        try { sessionStorage.removeItem('customer_type_private'); } catch (e) {}
        if (!this.hasB2BSignupCompleted()) {
          this.openB2BModal();
        }
      } else {
        this.selectedType = 'private';
        this.clearB2BData();
        this.storePrivateSelection();
      }
      this.enableCheckout();
    });

    this.enableCheckout();
  }

  hasB2BSignupCompleted() {
    try {
      const data = sessionStorage.getItem('b2b_signup_data');
      if (!data) return false;

      const signupData = JSON.parse(data);
      return signupData.completed === true;
    } catch (error) {
      return false;
    }
  }

  hasPrivateSelectionStored() {
    try {
      return sessionStorage.getItem('customer_type_private') === 'true';
    } catch (error) {
      return false;
    }
  }

  storePrivateSelection() {
    try {
      sessionStorage.setItem('customer_type_private', 'true');
    } catch (error) {
      // silently fail
    }
  }

  getCheckoutButtons() {
    if (this.context === 'drawer') {
      return document.querySelectorAll('#cart-drawer button[name="checkout"], #cart-drawer a[href*="checkout"]');
    } else {
      return document.querySelectorAll('button[name="checkout"], .cart-form button[type="submit"]');
    }
  }

  disableCheckout() {
    this.checkoutButtons.forEach(button => {
      button.disabled = true;
      button.style.opacity = '0.5';
      button.style.cursor = 'not-allowed';
    });
  }

  enableCheckout() {
    this.checkoutButtons.forEach(button => {
      button.disabled = false;
      button.style.opacity = '';
      button.style.cursor = '';
    });
  }

  handleSelectionChange(event) {
    this.selectedType = event.target.value;
    this.clearError();

    if (this.selectedType === 'private') {
      this.clearB2BData();
      this.storePrivateSelection();
      this.enableCheckout();
    } else if (this.selectedType === 'b2b') {
      try { sessionStorage.removeItem('customer_type_private'); } catch (e) {}
      if (this.hasB2BSignupCompleted()) {
        // Don't open modal, just enable checkout
        this.enableCheckout();
      } else {
        // Not completed yet, open modal for signup
        this.openB2BModal();
      }
    }
  }

  clearB2BData() {
    // Clear sessionStorage
    try {
      sessionStorage.removeItem('b2b_signup_data');
    } catch (error) {
      console.error('Error clearing B2B sessionStorage:', error);
    }

    // Clear VAT ID from cart attributes
    this.clearVatIdFromCart();
  }

  async clearVatIdFromCart() {
    try {
      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            vat_id: '',
            vat_invalid: ''
          }
        })
      });

      // Don't trigger cart:refresh event here to avoid re-rendering the customer type selector
      // The VAT ID is not visible in the UI, so no refresh needed
    } catch (error) {
      console.error('Error clearing VAT ID from cart:', error);
    }
  }

  openB2BModal() {
    // Disable checkout until B2B form is completed
    this.disableCheckout();
    // Create or show B2B modal
    if (!this.modal) {
      this.createB2BModal();
    }
    this.modal.show();
  }

  createB2BModal() {
    // Find pre-rendered B2B modal in the DOM (from overlay-group)
    this.modal = document.querySelector('#b2b-modal');

    if (this.modal) {

      // Listen for modal completion
      this.modal.addEventListener('b2b-completed', () => {
        this.enableCheckout();
      });

      // Listen for modal cancellation
      this.modal.addEventListener('b2b-cancelled', () => {
        this.clearB2BData();
        this.resetSelection();
      });
    } else {
      console.error('B2B modal not found. Please add b2b-signup-modal section to overlay-group.');
    }
  }

  resetSelection() {
    if (this.checkbox) {
      this.checkbox.checked = false;
      this.selectedType = 'private';
      this.enableCheckout();
      return;
    }
    this.radioButtons.forEach(radio => radio.checked = false);
    this.selectedType = null;
    this.disableCheckout();
  }

  interceptCheckoutButtons() {
    this.checkoutButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        if (!this.selectedType) {
          event.preventDefault();
          this.showError();
        }
      });
    });
  }

  showError() {
    this.classList.add('customer-type-selector--error');
  }

  clearError() {
    this.classList.remove('customer-type-selector--error');
  }
}


// B2B Modal Web Component - Extends theme's Drawer class
// Handles B2B signup form submission to backend API
class B2BModal extends window.Drawer {
  constructor() {
    super();
    this.skipVatValidation = false;
  }

  connectedCallback() {
    super.connectedCallback();

    // Get form reference
    this.form = this.querySelector('.b2b-signup-form');

    // Initialize form handling when modal is shown
    this.addEventListener('dialog:after-show', () => {
      this.formCompleted = false;
      this.skipVatValidation = false;
      this.clearFormErrors();
      this.repopulateFormData();
    });

    // Setup form submission handler
    this.setupFormHandler();

    // Setup VAT error UI button handlers
    this.setupVATErrorHandlers();

    // Check for URL parameter to open modal programmatically
    this.checkURLParameter();
  }

  get openFrom() {
    return 'right';
  }

  async hide() {
    if (this.isHiding) return;
    this.isHiding = true;

    const eventType = this.formCompleted ? 'b2b-completed' : 'b2b-cancelled';
    this.dispatchEvent(new CustomEvent(eventType, { bubbles: true }));

    this.formCompleted = false;
    this.hideVATError();

    const result = await super.hide();
    this.isHiding = false;

    return result;
  }

  setupFormHandler() {
    if (!this.form) return;

    // Prevent duplicate event listeners if connectedCallback is called multiple times
    if (this.form.dataset.handlerAttached) return;
    this.form.dataset.handlerAttached = 'true';

    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // Clear previous errors
      this.clearFormErrors();

      // Get form data
      const formData = new FormData(this.form);
      const email = formData.get('email')?.trim();
      const companyName = formData.get('company_name')?.trim();
      const vatId = formData.get('vat_id')?.trim();

      // Client-side validation
      if (!email) {
        this.showFormError(window.themeVariables?.strings?.formErrorEmailRequired || 'Email is required');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.showFormError(window.themeVariables?.strings?.formErrorEmailInvalid || 'Please enter a valid email address');
        return;
      }

      if (!companyName) {
        this.showFormError(window.themeVariables?.strings?.formErrorCompanyRequired || 'Company name is required');
        return;
      }

      if (!vatId) {
        this.showFormError(window.themeVariables?.strings?.formErrorVatRequired || 'VAT ID is required');
        return;
      }

      // Validate VAT country code prefix (client-side)
      const countryValidation = this.validateVATCountryCode(vatId);
      if (!countryValidation.valid) {
        this.showVATCountryError(this.form.querySelector('#b2b-vat-id'), countryValidation.error);
        return;
      }

      // Store values for later use
      this.submittedVatId = vatId;
      this.submittedEmail = email;

      // Set loading state
      this.setLoading(true);

      try {
        const response = await fetch('/apps/integration/customer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            company_name: companyName,
            vat_id: vatId,
            skip_vat_validation: this.skipVatValidation
          })
        });

        const result = await response.json();

        // 200 = Success - customer was created/updated
        if (response.ok) {
          await this.handleSuccessfulSubmission(result);
          return;
        }

        // 422 = VAT issues (invalid VAT or VAT service errors) - show "Try Again / Continue Anyway"
        if (response.status === 422) {
          this.showVATError(result.errorCode || null, result.error || null);
          return;
        }

        // 400 = Hard errors (Shopify failures, missing params) - show form error
        this.showFormError(result.error || 'An error occurred. Please try again.');

      } catch (error) {
        console.error('B2B signup error:', error);
        this.showFormError(window.themeVariables?.strings?.formErrorGeneric || 'An error occurred. Please try again.');
      } finally {
        this.setLoading(false);
      }
    });

    // Clear VAT country error when user types in VAT field
    const vatField = this.form.querySelector('#b2b-vat-id');
    if (vatField) {
      vatField.addEventListener('input', () => {
        this.clearVATCountryError(vatField);
        this.skipVatValidation = false;
      });
    }

    // Save form data as user types (for repopulation if modal closes)
    const formFields = this.form.querySelectorAll('input');
    formFields.forEach(field => {
      field.addEventListener('input', () => {
        this.saveFormData();
      });
    });
  }

  async handleSuccessfulSubmission(result) {
    await this.saveVatIdToCart(result.vatValid);

    try {
      const checkoutUrl = await this.fetchCheckoutUrlWithEmail(this.submittedEmail);
      this.formCompleted = true;
      this.setB2BSignupCompleted();
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to set buyer email on cart:', error);
      this.showFormError(
        window.themeVariables?.strings?.formErrorCheckoutEmail
          || 'Could not prepare checkout. Please try again.'
      );
    }
  }

  setLoading(isLoading) {
    const submitBtn = this.form?.querySelector('.b2b-signup-form__submit');
    const submitText = this.form?.querySelector('.b2b-signup-form__submit-text');
    const loadingText = this.form?.querySelector('.b2b-signup-form__submit-loading');

    if (submitBtn) {
      submitBtn.disabled = isLoading;
    }
    if (submitText) {
      submitText.style.display = isLoading ? 'none' : '';
    }
    if (loadingText) {
      loadingText.style.display = isLoading ? '' : 'none';
    }
  }

  showFormError(message) {
    const errorContainer = this.form?.querySelector('.b2b-signup-form__error');
    const errorMessage = this.form?.querySelector('.b2b-signup-form__error-message');

    if (errorContainer && errorMessage) {
      errorMessage.textContent = message;
      errorContainer.style.display = 'block';
    }
  }

  clearFormErrors() {
    const errorContainer = this.form?.querySelector('.b2b-signup-form__error');
    if (errorContainer) {
      errorContainer.style.display = 'none';
    }

    // Clear VAT country error if present
    const vatField = this.form?.querySelector('#b2b-vat-id');
    if (vatField) {
      this.clearVATCountryError(vatField);
    }
  }

  async fetchCheckoutUrlWithEmail(email) {
    if (!email) throw new Error('email required');

    const callOnce = async () => {
      const cart = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      const res = await fetch('/apps/integration/cart-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cartToken: cart.token, email }),
      });

      return res;
    };

    let res = await callOnce();

    // Retry once on 422 — typically a stale cart token after rotation
    if (res.status === 422) {
      await new Promise((r) => setTimeout(r, 100));
      res = await callOnce();
    }

    if (!res.ok) {
      throw new Error(`cart-email failed: ${res.status}`);
    }

    const { checkoutUrl } = await res.json();
    if (!checkoutUrl) throw new Error('cart-email returned no checkoutUrl');

    return checkoutUrl;
  }

  async saveVatIdToCart(vatValid) {
    if (!this.submittedVatId) return;

    try {
      const attributes = {
        vat_id: this.submittedVatId
      };

      // Set vat_invalid attribute if VAT was not valid (user bypassed validation)
      if (!vatValid) {
        attributes.vat_invalid = 'true';
      }

      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (error) {
      console.error('Error saving VAT ID to cart:', error);
    }
  }

  setupVATErrorHandlers() {
    // Prevent duplicate event listeners
    if (this.vatErrorHandlersAttached) return;
    this.vatErrorHandlersAttached = true;

    const tryAgainBtn = this.querySelector('.b2b-modal__try-again');
    const continueAnywayBtn = this.querySelector('.b2b-modal__continue-anyway');

    if (tryAgainBtn) {
      tryAgainBtn.addEventListener('click', () => {
        this.hideVATError();
      });
    }

    if (continueAnywayBtn) {
      continueAnywayBtn.addEventListener('click', () => {
        // Set skip flag and re-submit form
        this.skipVatValidation = true;
        this.hideVATError();
        this.form?.requestSubmit();
      });
    }
  }

  showVATError(errorCode = null, errorMessage = null) {
    const formContainer = this.querySelector('.b2b-modal__form-container');
    const errorContainer = this.querySelector('.b2b-modal__vat-error');
    const additionalErrorEl = this.querySelector('.b2b-modal__vat-error-details');

    if (formContainer) formContainer.style.display = 'none';
    if (errorContainer) errorContainer.style.display = 'block';

    if (additionalErrorEl) {
      const translatedError = this.getVATErrorMessage(errorCode);
      if (translatedError) {
        additionalErrorEl.textContent = translatedError;
        additionalErrorEl.style.display = 'block';
      } else {
        additionalErrorEl.textContent = '';
        additionalErrorEl.style.display = 'none';
      }
    }
  }

  getVATErrorMessage(errorCode) {
    if (!errorCode) return null;

    const errorMessages = {
      'INVALID_INPUT': window.themeVariables?.strings?.vatErrorInvalidInput,
      'INVALID_VAT': window.themeVariables?.strings?.vatErrorInvalidInput,
      'SERVICE_UNAVAILABLE': window.themeVariables?.strings?.vatErrorServiceUnavailable,
      'DATABASE_UNAVAILABLE': window.themeVariables?.strings?.vatErrorDatabaseUnavailable
    };

    return errorMessages[errorCode] || null;
  }

  hideVATError() {
    const formContainer = this.querySelector('.b2b-modal__form-container');
    const errorContainer = this.querySelector('.b2b-modal__vat-error');
    const additionalErrorEl = this.querySelector('.b2b-modal__vat-error-details');

    if (formContainer) formContainer.style.display = '';
    if (errorContainer) errorContainer.style.display = 'none';
    if (additionalErrorEl) {
      additionalErrorEl.textContent = '';
      additionalErrorEl.style.display = 'none';
    }
  }

  checkURLParameter() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('b2b_signup')) {
      setTimeout(() => {
        this.show();
      }, 500);

      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('b2b_signup');
      window.history.replaceState({}, '', newUrl);
    }
  }

  // Session storage helpers
  getB2BSignupData() {
    try {
      const data = sessionStorage.getItem('b2b_signup_data');
      if (!data) return { completed: false };
      return JSON.parse(data);
    } catch (error) {
      return { completed: false };
    }
  }

  saveB2BSignupData(updates) {
    try {
      const current = this.getB2BSignupData();
      const merged = { ...current, ...updates };
      sessionStorage.setItem('b2b_signup_data', JSON.stringify(merged));
    } catch (error) {
      console.error('Error saving B2B signup data:', error);
    }
  }

  setB2BSignupCompleted() {
    this.saveB2BSignupData({ completed: true });
  }

  saveFormData() {
    if (!this.form) return;

    const formData = {
      email: this.form.querySelector('#b2b-email')?.value || '',
      company_name: this.form.querySelector('#b2b-company-name')?.value || '',
      vat_id: this.form.querySelector('#b2b-vat-id')?.value || ''
    };

    this.saveB2BSignupData({ formData });
  }

  repopulateFormData() {
    if (!this.form) return;

    const signupData = this.getB2BSignupData();
    const formData = signupData.formData;
    if (!formData) return;

    const emailField = this.form.querySelector('#b2b-email');
    const companyField = this.form.querySelector('#b2b-company-name');
    const vatField = this.form.querySelector('#b2b-vat-id');

    if (emailField && formData.email) emailField.value = formData.email;
    if (companyField && formData.company_name) companyField.value = formData.company_name;
    if (vatField && formData.vat_id) vatField.value = formData.vat_id;
  }

  // VAT country code validation (client-side prefix check)
  validateVATCountryCode(vatNumber) {
    const shippingCountries = this.getAttribute('data-shipping-countries');
    if (!shippingCountries) {
      return { valid: true, countryCode: '', error: null };
    }

    const enabledCountries = shippingCountries.split(',').map(c => c.trim().toUpperCase());
    const cleanVAT = vatNumber.replace(/\s/g, '').toUpperCase();

    if (cleanVAT.length < 2) {
      const error = window.themeVariables?.strings?.vatCountryMinLength || 'VAT number must be at least 2 characters';
      return { valid: false, countryCode: '', error };
    }

    const vatCountryCode = cleanVAT.substring(0, 2);

    // Special handling for GB/XI (UK and Northern Ireland)
    const hasGB = enabledCountries.includes('GB');
    if (hasGB && (vatCountryCode === 'GB' || vatCountryCode === 'XI')) {
      return { valid: true, countryCode: vatCountryCode, error: null };
    }

    // Special handling for Greece (country code GR, but VAT prefix is EL)
    const hasGR = enabledCountries.includes('GR');
    if (hasGR && vatCountryCode === 'EL') {
      return { valid: true, countryCode: vatCountryCode, error: null };
    }

    if (!enabledCountries.includes(vatCountryCode)) {
      const allowedPrefixes = [...enabledCountries];
      // Add XI after GB for display
      if (hasGB) {
        const gbIndex = allowedPrefixes.indexOf('GB');
        allowedPrefixes.splice(gbIndex + 1, 0, 'XI');
      }
      // Replace GR with EL for display (Greek VAT numbers use EL prefix)
      if (hasGR) {
        const grIndex = allowedPrefixes.indexOf('GR');
        allowedPrefixes[grIndex] = 'EL';
      }

      let error;
      if (allowedPrefixes.length === 1) {
        const message = window.themeVariables?.strings?.vatCountryInvalidSingle || 'VAT number must start with {{country}}';
        error = message.replace('{{country}}', allowedPrefixes[0]);
      } else {
        const message = window.themeVariables?.strings?.vatCountryInvalidMultiple || 'VAT number must start with one of: {{countries}}';
        error = message.replace('{{countries}}', allowedPrefixes.join(', '));
      }
      return { valid: false, countryCode: vatCountryCode, error };
    }

    return { valid: true, countryCode: vatCountryCode, error: null };
  }

  showVATCountryError(vatField, errorMessage) {
    if (!vatField) return;

    let errorElement = vatField.parentElement.querySelector('.vat-country-error');

    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'vat-country-error';
      errorElement.style.cssText = 'color: #d72c0d; font-size: 0.875rem; margin-top: 0.5rem;';
      vatField.parentElement.appendChild(errorElement);
    }

    errorElement.textContent = errorMessage;
    vatField.setAttribute('aria-invalid', 'true');
  }

  clearVATCountryError(vatField) {
    if (!vatField) return;

    const errorElement = vatField.parentElement.querySelector('.vat-country-error');
    if (errorElement) {
      errorElement.remove();
    }
    vatField.removeAttribute('aria-invalid');
  }
}

// Register web components
if (!window.customElements.get("member-signup-checkbox")) {
  window.customElements.define("member-signup-checkbox", MemberSignupCheckbox);
}

if (!window.customElements.get("customer-type-selector")) {
  window.customElements.define("customer-type-selector", CustomerTypeSelector);
}

if (!window.customElements.get("b2b-modal")) {
  window.customElements.define("b2b-modal", B2BModal);
}

if (!window.customElements.get("tax-toggle")) {
  window.customElements.define("tax-toggle", TaxToggle);
}

// Purchase Card Price Switcher — toggles between regular/member price radios
var PurchaseCardPrices = class extends HTMLElement {
  constructor() {
    super();
    this._updating = false;
  }

  connectedCallback() {
    if (!this.hasAttribute('data-switchable')) return;

    this.querySelectorAll('.price-card').forEach(card => {
      card.style.cursor = 'pointer';
      card.addEventListener('click', this._onCardClick.bind(this));
    });
  }

  _onCardClick(event) {
    if (this._updating) return;

    const card = event.currentTarget;
    const type = card.dataset.priceType;
    if (!type) return;

    if (card.classList.contains('price-card--active')) return;

    this._setActive(type);
    this._updateCartAttribute(type === 'member');
  }

  _setActive(type) {
    this.querySelectorAll('.price-card').forEach(card => {
      const isTarget = card.dataset.priceType === type;
      card.classList.toggle('price-card--active', isTarget);
      const radio = card.querySelector('.price-card__radio');
      if (radio) {
        radio.classList.toggle('price-card__radio--checked', isTarget);
      }
    });
  }

  async _updateCartAttribute(isMember) {
    this._updating = true;
    try {
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { new_member: isMember ? 'true' : '' }
        })
      });

      if (!response.ok) throw new Error('Cart update failed');

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      // Sync any member-signup checkboxes on the page
      document.querySelectorAll('member-signup-checkbox input[type="checkbox"]').forEach(cb => {
        cb.checked = isMember;
      });
    } catch (error) {
      console.error('Error updating member price selection:', error);
      this._setActive(isMember ? 'regular' : 'member');
    } finally {
      this._updating = false;
    }
  }
};

if (!window.customElements.get('purchase-card-prices')) {
  window.customElements.define('purchase-card-prices', PurchaseCardPrices);
}

// Tooltip — (i) button opens/closes tooltip popups via event delegation
// Supports both .cart-drawer__tooltip and .shipping-module__tooltip
(function () {
  var TOOLTIP_SEL = '.cart-drawer__tooltip, .shipping-module__tooltip';
  var CLOSE_SEL = '.cart-drawer__tooltip-close, .shipping-module__tooltip-close';

  function closeAllTooltips() {
    document.querySelectorAll(TOOLTIP_SEL + ':not([hidden])').forEach(function (t) {
      t.setAttribute('hidden', '');
      var line = t.closest('.shipping-module__line, .cart-drawer__line');
      if (line) line.classList.remove('tooltip-active');
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-tooltip-target]');
    if (btn) {
      e.stopPropagation();
      var id = btn.getAttribute('data-tooltip-target');
      var tooltip = document.getElementById(id);
      if (!tooltip) return;

      var wasHidden = tooltip.hasAttribute('hidden');
      closeAllTooltips();
      if (wasHidden) {
        tooltip.removeAttribute('hidden');
        var line = tooltip.closest('.shipping-module__line, .cart-drawer__line');
        if (line) line.classList.add('tooltip-active');
      }
      return;
    }

    if (e.target.closest(CLOSE_SEL)) {
      var tip = e.target.closest(TOOLTIP_SEL);
      if (tip) {
        tip.setAttribute('hidden', '');
        var line = tip.closest('.shipping-module__line, .cart-drawer__line');
        if (line) line.classList.remove('tooltip-active');
      }
      return;
    }

    if (!e.target.closest(TOOLTIP_SEL)) {
      closeAllTooltips();
    }
  });
})();

// Product Image Galleries Component
import { PhotoSwipeLightbox } from "vendor";

class ProductImageGalleries extends HTMLElement {
  constructor() {
    super();
    this.galleries = [];
    this._photoswipe = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Initialize each gallery
    this.querySelectorAll('.product-image-gallery').forEach((gallery, index) => {
      this.initializeGallery(gallery, index);
    });
  }

  initializeGallery(galleryElement, index) {
    const galleryType = galleryElement.getAttribute('data-gallery-type');
    const previewItems = galleryElement.querySelectorAll('.product-image-gallery__preview-item');
    const dataItems = galleryElement.querySelectorAll('.product-image-gallery__data-item');

    // Create data source for PhotoSwipe from the server-rendered data
    const dataSource = Array.from(dataItems).map((item, index) => {
      // Find the corresponding thumbnail image for msrc and dimensions
      const thumbnailImg = previewItems[index]?.querySelector('.product-image-gallery__preview-image');

      return {
        src: item.getAttribute('data-src'),
        msrc: thumbnailImg ? (thumbnailImg.currentSrc || thumbnailImg.src) : item.getAttribute('data-src'),
        alt: item.getAttribute('data-alt') || '',
        width: 0, // Will be set after thumbnails load
        height: 0, // Will be set after thumbnails load
        thumbnailElement: thumbnailImg || null
      };
    });

    // Wait for thumbnail images to load, then update dimensions
    this.waitForThumbnailsAndUpdateDimensions(dataSource, previewItems);

    // Add click handlers to preview items
    previewItems.forEach((item, itemIndex) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.openGallery(dataSource, itemIndex, galleryType);
      });
    });

    // Store gallery data for potential future use
    this.galleries[index] = {
      element: galleryElement,
      type: galleryType,
      dataSource: dataSource
    };
  }

  waitForThumbnailsAndUpdateDimensions(dataSource, previewItems) {
    // Get first 3 thumbnail images
    const thumbnailImages = Array.from(previewItems)
      .slice(0, 3)
      .map(item => item.querySelector('.product-image-gallery__preview-image'))
      .filter(img => img);

    if (thumbnailImages.length === 0) return;

    // Wait for each thumbnail to load
    const loadPromises = thumbnailImages.map((img, index) => {
      return new Promise((resolve) => {
        if (img.complete && img.naturalWidth > 0) {
          // Image already loaded
          resolve({ index, width: img.naturalWidth, height: img.naturalHeight });
        } else {
          // Wait for image to load
          img.onload = () => {
            resolve({ index, width: img.naturalWidth, height: img.naturalHeight });
          };
          img.onerror = () => {
            resolve({ index, width: 0, height: 0 }); // Fallback
          };
        }
      });
    });

    // Update dataSource when thumbnails are loaded
    Promise.all(loadPromises).then((results) => {
      results.forEach(({ index, width, height }) => {
        if (dataSource[index] && width > 0 && height > 0) {
          dataSource[index].width = width;
          dataSource[index].height = height;
        }
      });
    });
  }

  // Create PhotoSwipe instance once (like the theme does)
  get photoswipe() {
    if (this._photoswipe) {
      return this._photoswipe;
    }

    const photoswipe = new PhotoSwipeLightbox({
      pswpModule: () => import('photoswipe'),
      bgOpacity: 1,
      maxZoomLevel: 3,
      closeTitle: window.themeVariables?.strings?.closeGallery || 'Close gallery',
      zoomTitle: window.themeVariables?.strings?.zoomGallery || 'Zoom',
      errorMsg: window.themeVariables?.strings?.errorGallery || 'Image cannot be loaded',
      // UX settings to match theme
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      zoom: false,
      closeSVG: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
        <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
        <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
      </svg>`
    });

    // Handle unknown image sizes for images beyond the first 3 (fallback only)
    photoswipe.on('gettingData', (item) => {
      // The slide data is directly in item.data
      const slideData = item.data;

      // Skip if image already has dimensions (from thumbnails or background loading)
      if (slideData.width > 0 && slideData.height > 0) {
        return;
      }

      if (slideData.width < 1 || slideData.height < 1) { // unknown size
        const img = new Image();

        img.onload = function() {
          slideData.width = this.width; // set image width
          slideData.height = this.height; // set image height

          // Update PhotoSwipe with new dimensions (PhotoSwipe v5 API)
          if (photoswipe.pswp) {
            // Force PhotoSwipe to recalculate dimensions
            photoswipe.pswp.refreshSlideContent(photoswipe.pswp.currIndex);
            photoswipe.pswp.updateSize();
          }
        };

        img.onerror = function() {
          // Set fallback dimensions
          slideData.width = 800;
          slideData.height = 600;
        };

        img.src = slideData.src; // let's download image
      }
    });

    // Add bottom navigation bar (pagination) - same as theme
    photoswipe.on('uiRegister', () => {
      photoswipe.pswp.ui.registerElement({
        name: 'bottom-bar',
        order: 5,
        appendTo: 'wrapper',
        html: `
          <div class="pagination">
            <button class="pagination__item group" rel="prev">
              <span class="animated-arrow animated-arrow--reverse"></span>
            </button>
            <span class="pagination__current text-sm">
              <span class="pagination__current-page">1</span> / <span class="pagination__page-count"></span>
            </span>
            <button class="pagination__item group" rel="next">
              <span class="animated-arrow"></span>
            </button>
          </div>
        `,
        onInit: (el, pswp) => {
          el.querySelector('.pagination__page-count').innerText = pswp.getNumItems();
          el.querySelector('[rel="prev"]')?.addEventListener('click', () => pswp.prev());
          el.querySelector('[rel="next"]')?.addEventListener('click', () => pswp.next());
          pswp.on('change', () => {
            el.querySelector('.pagination__current-page').innerText = pswp.currIndex + 1;
          });
        }
      });
    });

    photoswipe.init();
    return this._photoswipe = photoswipe;
  }

  openGallery(dataSource, startIndex = 0, galleryType = '') {
    // Use the dataSource as-is (dimensions already set in initializeGallery)
    // First 3 images have thumbnail dimensions, rest have 0,0 for background loading
    this.photoswipe.loadAndOpen(startIndex, dataSource);

    // Background load dimensions for remaining images (4+)
    this.loadRemainingDimensions(dataSource);
  }

  loadRemainingDimensions(dataSource) {
    // Find images without dimensions (images 4+ that don't have thumbnail data)
    const unknownItems = dataSource.filter(item => item.width < 1 || item.height < 1);

    if (unknownItems.length === 0) return;

    // Load all remaining images in parallel
    unknownItems.forEach((item) => {
      const img = new Image();

      img.onload = () => {
        // Update the item with real dimensions
        item.width = img.naturalWidth;
        item.height = img.naturalHeight;

        // Update PhotoSwipe if this image is currently visible
        if (this.photoswipe.pswp && this.photoswipe.pswp.currIndex !== undefined) {
          const currentSlide = this.photoswipe.pswp.currSlide;
          // Check if this is the currently visible slide
          if (currentSlide && currentSlide.data.src === item.src) {
            this.photoswipe.pswp.refreshSlideContent(this.photoswipe.pswp.currIndex);
            this.photoswipe.pswp.updateSize();
          }
        }
      };

      img.onerror = () => {
        // Set fallback dimensions
        item.width = 800;
        item.height = 600;
      };

      img.src = item.src;
    });
  }
}

// Register the custom element
if (!window.customElements.get('product-image-galleries')) {
  window.customElements.define('product-image-galleries', ProductImageGalleries);
}

// Custom Shipping Calculator Web Component - adapted from theme's ShippingEstimator
class CustomShippingCalculator extends HTMLElement {
  // API endpoint for shipping rate calculations
  static SHIPPING_RATES_ENDPOINT = '/apps/integration/shipping-rates';

  constructor() {
    super();
    this._estimateShippingListener = this._estimateShipping.bind(this);
  }

  connectedCallback() {
    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.querySelector('.shipping-calculator__results');

    if (this.submitButton) {
      this.submitButton.addEventListener('click', this._estimateShippingListener);
    }
  }

  disconnectedCallback() {
    if (this.submitButton) {
      this.submitButton.removeEventListener('click', this._estimateShippingListener);
    }
  }

  async _estimateShipping(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]')?.value;
    const country = this.querySelector('[name="address[country]"]')?.value;
    const province = this.querySelector('[name="address[province]"]')?.value;

    if (!zip || !country) {
      this._formatError({ general: 'Please fill in all required fields' });
      this.resultsElement.hidden = false;
      return;
    }

    // Get product and variant data
    const productId = this.dataset.productId;
    const variantId = this.dataset.variantId;

    if (!productId || !variantId) {
      this._formatError({ general: 'Product information not found' });
      this.resultsElement.hidden = false;
      return;
    }

    // Get quantity from product form's quantity selector
    let quantity = 1; // Default fallback
    const quantitySelector = document.querySelector('quantity-selector input[name="quantity"]');
    if (quantitySelector && quantitySelector.value) {
      quantity = parseInt(quantitySelector.value) || 1;
    }

    this.submitButton.setAttribute('aria-busy', 'true');

    try {
      const body = JSON.stringify({
        variantId: variantId,
        quantity: quantity,
        currency: window.Shopify.currency.active,
        locale: window.Shopify.locale,
        shipping_address: { zip, country, province }
      });

      // Use the same custom backend as omhucph theme
      const response = await fetch(CustomShippingCalculator.SHIPPING_RATES_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.shipping_rates) {
        this._formatShippingRates(data.shipping_rates);
      } else {
        this._formatError(data.error || { general: 'Unable to calculate shipping rates' });
      }
    } catch (error) {
      console.error('Shipping calculation error:', error);
      this._formatError({ general: 'Unable to calculate shipping rates. Please try again.' });
    } finally {
      this.resultsElement.hidden = false;
      this.submitButton.removeAttribute('aria-busy');
    }
  }

  _formatShippingRates(shippingRates) {
    let formattedShippingRates = shippingRates.map((shippingRate) => {
      return `<li>${shippingRate["presentment_name"] || shippingRate["name"]}: ${window.theme.formatMoney(shippingRate["price"], { currency: shippingRate["currency"] })}</li>`;
    });

    // Use the same strings as the theme's ShippingEstimator
    let message = '';
    if (shippingRates.length === 0) {
      message = window.themeVariables?.strings?.shippingEstimatorNoResults || 'Sorry, we do not ship to your address.';
    } else if (shippingRates.length === 1) {
      message = window.themeVariables?.strings?.shippingEstimatorOneResult || 'There is one shipping rate for your address:';
    } else {
      message = window.themeVariables?.strings?.shippingEstimatorMultipleResults || 'There are several shipping rates for your address:';
    }

    this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${message}</p>
        ${formattedShippingRates.length > 0 ? `<ul class="list-disc" role="list">${formattedShippingRates.join("")}</ul>` : ''}
      </div>
    `;
  }

  _formatError(errors) {
    let formattedErrors = [];

    if (typeof errors === 'string') {
      formattedErrors = [`<li>${errors}</li>`];
    } else if (typeof errors === 'object') {
      formattedErrors = Object.keys(errors).map((errorKey) => {
        return `<li>${errors[errorKey]}</li>`;
      });
    }

    const errorMessage = window.themeVariables?.strings?.shippingEstimatorError || 'One or more error occurred while retrieving shipping rates:';

    this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${errorMessage}</p>
        <ul class="list-disc" role="list">${formattedErrors.join('')}</ul>
      </div>
    `;
  }
}

if (!window.customElements.get("custom-shipping-calculator")) {
  window.customElements.define("custom-shipping-calculator", CustomShippingCalculator);
}

/* Draft order */
function raqCustomizeFormEmbed(hostElement) {
  const shadowRoot = hostElement.shadowRoot;
  if (!shadowRoot) {
    return;
  }

  /*
  const internalStyle = document.createElement('style');
  internalStyle.textContent = `
    #custom\\#product,
    #label-custom\\#product {
      display: none;
    }
  `;
  shadowRoot.appendChild(internalStyle);
  */

  // set drawer layout
  const appBlockHolder = hostElement.closest('.app-block-holder');
  if (appBlockHolder) {
    const shopifyAppBlock = appBlockHolder.querySelector('.shopify-app-block');
    var appBlockTitle = '';
    var textHeadingElement = shadowRoot.querySelector('h1[class*="_textHeading_"], h2[class*="_textHeading_"], h3[class*="_textHeading_"]');
    if (textHeadingElement) {
      appBlockTitle = textHeadingElement.innerText.trim();
    }

    const appDrawerHolder = appBlockHolder.querySelector('.app-drawer-holder');
    if (appDrawerHolder) {
      const drawerContentElement = appDrawerHolder.querySelector('.product-details__drawer-content');
      const drawerTitleElements = appDrawerHolder.querySelectorAll('.product-details__drawer-title, .product-details__drawer-header');
      if (appBlockTitle != '' && drawerTitleElements.length) {
        drawerTitleElements.forEach((element) => {
          element.innerText = appBlockTitle;
        });
      }

      if (appBlockTitle != '' && shopifyAppBlock && drawerContentElement) {
        if (shopifyAppBlock.parentElement !== drawerContentElement) {
          drawerContentElement.appendChild(shopifyAppBlock);
        }
        appDrawerHolder.classList.remove('hidden');
      }
    } else {
      const appAccordionHolder = appBlockHolder.querySelector('.app-accordion-holder');
      if (appAccordionHolder) {
        const accordionContentElement = appAccordionHolder.querySelector('.accordion__content');
        const accordionTitleElement = appAccordionHolder.querySelector('.accordion__title');

        if (appBlockTitle != '' && accordionTitleElement) {
          accordionTitleElement.innerText = appBlockTitle;
        }

        if (appBlockTitle != '' && shopifyAppBlock && accordionContentElement) {
          if (shopifyAppBlock.parentElement !== accordionContentElement) {
            accordionContentElement.appendChild(shopifyAppBlock);
          }
          appAccordionHolder.classList.remove('hidden');
        }
      }
    }
  }

  const productInput = shadowRoot.querySelector('#custom\\#product');
  if (productInput) {
    var productTitle = '';
    var productTitleElement = document.querySelector('.product-info__title');
    if (productTitleElement) {
      productTitle = productTitleElement.innerText;
    }
    const productURL = window.location.href;

    productInput.value = productTitle + ': ' + productURL;
    productInput.dispatchEvent(new Event('input', { bubbles: true }));
    productInput.dispatchEvent(new Event('change', { bubbles: true }));

    productInput.parentElement.style.display = 'none';
  }

  const quantityInput = shadowRoot.querySelector('#custom\\#quantity');
  if (quantityInput) {
    var quantity = 1;

    const productFormQuantityInput = document.querySelector('.product-info__quantity-selector input[name="quantity"]');
    if (productFormQuantityInput) {
      quantity = productFormQuantityInput.value;

      productFormQuantityInput.addEventListener('input', (event) => {
        const newQuantity = event.target.value;
        quantityInput.value = newQuantity;
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });

      productFormQuantityInput.addEventListener('change', (event) => {
        const newQuantity = event.target.value;
        quantityInput.value = newQuantity;
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    quantityInput.value = quantity;
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

    // quantityInput.parentElement.style.display = 'none';
  }
}

function raqStartObserving() {
  const container = document.querySelector('.shopify-section--main-product');
  if (!container) {
    return;
  }

  const observer = new MutationObserver((mutationsList, observer) => {
    const formEmbedHost = container.querySelector('#app-embed');
    if (formEmbedHost) {
      raqCustomizeFormEmbed(formEmbedHost);
      observer.disconnect();
    }
  });

  const config = { childList: true, subtree: true };
  observer.observe(container, config);
}
raqStartObserving();

let productDescriptionClampFrame = null;
let productDescriptionClampResizeObserver = null;
const productDescriptionClampObservedContents = new Set();

function updateProductDescriptionClampToggle(container) {
  const toggle = container.querySelector('[data-description-clamp-toggle]');
  if (!toggle) {
    return;
  }

  const isExpanded = container.dataset.clampState === 'expanded';
  const collapsedLabel = toggle.dataset.collapsedLabel || 'Read more';
  const expandedLabel = toggle.dataset.expandedLabel || collapsedLabel;

  toggle.textContent = isExpanded ? expandedLabel : collapsedLabel;
  toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
}

function observeProductDescriptionClampContent(content) {
  if (!content || productDescriptionClampObservedContents.has(content)) {
    return;
  }

  productDescriptionClampObservedContents.add(content);

  if (!('ResizeObserver' in window)) {
    return;
  }

  if (!productDescriptionClampResizeObserver) {
    productDescriptionClampResizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const container = entry.target.closest('[data-description-clamp]');

        if (container) {
          requestProductDescriptionClampSync(container);
        }
      });
    });
  }

  productDescriptionClampResizeObserver.observe(content);
}

function cleanupProductDescriptionClampObservers() {
  if (!productDescriptionClampResizeObserver) {
    return;
  }

  productDescriptionClampObservedContents.forEach((content) => {
    if (content.isConnected) {
      return;
    }

    productDescriptionClampResizeObserver.unobserve(content);
    productDescriptionClampObservedContents.delete(content);
  });
}

function syncProductDescriptionClamps(root = document) {
  if (!root) {
    return;
  }

  const containers = [];

  if (root.matches && root.matches('[data-description-clamp]')) {
    containers.push(root);
  }

  if (root.querySelectorAll) {
    root.querySelectorAll('[data-description-clamp]').forEach((container) => {
      containers.push(container);
    });
  }

  containers.forEach((container) => {
    const content = container.querySelector('[data-description-clamp-content]');
    const toggle = container.querySelector('[data-description-clamp-toggle]');

    if (!content || !toggle) {
      return;
    }

    observeProductDescriptionClampContent(content);

    const lineCount = parseInt(container.dataset.clampLines || '4', 10);
    const computedStyle = window.getComputedStyle(content);
    let lineHeight = parseFloat(computedStyle.lineHeight);

    if (!Number.isFinite(lineHeight)) {
      const fontSize = parseFloat(computedStyle.fontSize);
      lineHeight = Number.isFinite(fontSize) ? fontSize * 1.5 : 24;
    }

    const collapsedHeight = Math.ceil(lineHeight * lineCount);
    const previousState = container.dataset.clampState === 'expanded' ? 'expanded' : 'collapsed';

    container.style.setProperty('--product-description-clamp-height', `${collapsedHeight}px`);

    // Temporarily un-clamp so scrollHeight reflects the full content height.
    container.dataset.hasOverflow = 'false';
    container.dataset.clampState = 'expanded';

    const hasOverflow = content.scrollHeight > collapsedHeight + 1;
    container.dataset.hasOverflow = hasOverflow ? 'true' : 'false';

    if (!hasOverflow) {
      container.dataset.clampState = 'expanded';
      toggle.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      return;
    }

    container.dataset.clampState = previousState;
    toggle.hidden = false;
    updateProductDescriptionClampToggle(container);
  });
}

function requestProductDescriptionClampSync(root = document) {
  cleanupProductDescriptionClampObservers();

  if (productDescriptionClampFrame !== null) {
    cancelAnimationFrame(productDescriptionClampFrame);
  }

  productDescriptionClampFrame = requestAnimationFrame(() => {
    syncProductDescriptionClamps(root);
    productDescriptionClampFrame = null;
  });
}

document.addEventListener('click', function (event) {
  const toggle = event.target.closest('[data-description-clamp-toggle]');

  if (!toggle) {
    return;
  }

  const container = toggle.closest('[data-description-clamp]');

  if (!container || container.dataset.hasOverflow !== 'true') {
    return;
  }

  container.dataset.clampState = container.dataset.clampState === 'expanded' ? 'collapsed' : 'expanded';
  updateProductDescriptionClampToggle(container);
});

document.addEventListener('DOMContentLoaded', function () {
  requestProductDescriptionClampSync(document);
});

document.addEventListener('shopify:section:load', function (event) {
  requestProductDescriptionClampSync(event.target);
});

window.addEventListener('resize', function () {
  requestProductDescriptionClampSync(document);
});

window.addEventListener('load', function () {
  requestProductDescriptionClampSync(document);
});
/* end Draft order */


/* Exploded View Lightbox - Single image PhotoSwipe overlay */
import { PhotoSwipeLightbox as ExplodedViewPhotoSwipe } from "vendor";

class ExplodedViewLightbox extends HTMLElement {
  constructor() {
    super();
    this._photoswipe = null;
    this._boundHandleClick = this.handleClick.bind(this);
    this._boundHandleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    const trigger = this.querySelector('.exploded-view-thumbnail');
    if (!trigger) return;

    trigger.addEventListener('click', this._boundHandleClick);
    trigger.addEventListener('keydown', this._boundHandleKeydown);
  }

  disconnectedCallback() {
    const trigger = this.querySelector('.exploded-view-thumbnail');
    if (trigger) {
      trigger.removeEventListener('click', this._boundHandleClick);
      trigger.removeEventListener('keydown', this._boundHandleKeydown);
    }

    // Clean up PhotoSwipe instance
    if (this._photoswipe) {
      this._photoswipe.destroy();
      this._photoswipe = null;
    }
  }

  handleClick(event) {
    event.preventDefault();
    this.openLightbox();
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openLightbox();
    }
  }

  get photoswipe() {
    if (this._photoswipe) {
      return this._photoswipe;
    }

    // Match exact theme PhotoSwipe configuration
    const photoswipe = new ExplodedViewPhotoSwipe({
      pswpModule: () => import('photoswipe'),
      bgOpacity: 1,
      maxZoomLevel: 3,
      closeTitle: window.themeVariables?.strings?.closeGallery || 'Close gallery',
      zoomTitle: window.themeVariables?.strings?.zoomGallery || 'Zoom',
      errorMsg: window.themeVariables?.strings?.errorGallery || 'Image cannot be loaded',
      // UX - match theme settings exactly
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      zoom: false,
      closeSVG: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
        <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
        <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
      </svg>`
    });

    // Handle unknown image dimensions (fallback for when aspect ratio can't be determined)
    photoswipe.on('gettingData', (item) => {
      const slideData = item.data;
      if (slideData.width < 1 || slideData.height < 1) {
        const img = new Image();
        img.onload = function() {
          slideData.width = this.width;
          slideData.height = this.height;
          if (photoswipe.pswp) {
            photoswipe.pswp.refreshSlideContent(photoswipe.pswp.currIndex);
            photoswipe.pswp.updateSize();
          }
        };
        img.onerror = function() {
          // Fallback dimensions
          slideData.width = 1200;
          slideData.height = 900;
        };
        img.src = slideData.src;
      }
    });

    photoswipe.init();
    return this._photoswipe = photoswipe;
  }

  openLightbox() {
    const fullSrc = this.dataset.fullSrc;

    // Edge case: no source URL
    if (!fullSrc) {
      console.warn('ExplodedViewLightbox: No full source URL provided');
      return;
    }

    const thumbnailImg = this.querySelector('.exploded-view-image');

    // Calculate dimensions - use thumbnail natural dimensions or fallback
    let width = 2000;
    let height = 1500; // Default 4:3 aspect ratio fallback

    if (thumbnailImg && thumbnailImg.naturalWidth > 0 && thumbnailImg.naturalHeight > 0) {
      const aspectRatio = thumbnailImg.naturalWidth / thumbnailImg.naturalHeight;
      height = Math.round(width / aspectRatio);
    }

    const dataSource = [{
      src: fullSrc,
      width: width,
      height: height,
      alt: thumbnailImg?.alt || 'Exploded view diagram',
      // Provide thumbnail element for smooth animation
      thumbnailElement: thumbnailImg
    }];

    this.photoswipe.loadAndOpen(0, dataSource);
  }
}
customElements.define('exploded-view-lightbox', ExplodedViewLightbox);
/* end Exploded View Lightbox */

/* Changes 2026/03/13 (ABU) */

// Separate price decimals
function formatPriceNodes() {
  const elements = document.querySelectorAll(
    '.product-info__purchase-card .normal-price-section .text-lg, .product-info__purchase-card .member-price-red'
  );
  elements.forEach(function (el) {
    // Add class to the existing "incl. VAT" span (if present)
    el.querySelectorAll('span.text-xs.text-subdued').forEach(function (span) {
      span.classList.add('price-vat-note');
    });
    // Prevent double processing
    if (el.querySelector('.price-decimals')) return;
    // Find the first meaningful TEXT node inside the element
    let textNode = null;
    for (let i = 0; i < el.childNodes.length; i++) {
      const n = el.childNodes[i];
      if (
        n.nodeType === Node.TEXT_NODE &&
        n.nodeValue &&
        n.nodeValue.replace(/\s+/g, '').length
      ) {
        textNode = n;
        break;
      }
    }
    if (!textNode) return;
    const raw = (textNode.nodeValue || '').replace(/\s+/g, ' ').trim();
    // Match: whole + separator + 2 decimals + optional currency
    const m = raw.match(/^(.+?)([.,])(\d{2})(\s*(?:[^\d\s].*|[A-Z]{3})\s*)?$/);
    if (!m) return;
    const whole = m[1].trim();
    const sep = m[2];
    const dec = m[3];
    const currency = (m[4] || '').replace(/\s+/g, ' ');
    // Replace only that text node with:
    // whole + <span class="price-decimals">,44 €</span>
    const decimalsSpan = document.createElement('span');
    decimalsSpan.className = 'price-decimals';
    decimalsSpan.textContent = sep + dec + currency;
    el.insertBefore(document.createTextNode(whole), textNode);
    el.insertBefore(decimalsSpan, textNode);
    el.removeChild(textNode);
  });
}
// Run on DOM ready
document.addEventListener('DOMContentLoaded', formatPriceNodes);
// Optional: expose to rerun after variant/price updates
window.formatPriceNodes = formatPriceNodes;
// End separate price decimals

// ---------------------------------------------------------------------------
// Collection infinite scroll (SEO-friendly: paginated URLs remain crawlable)
// ---------------------------------------------------------------------------
(function initCollectionInfiniteScroll() {
  const LOADING_DELAY_MS = 1250;

  function getCollectionInfiniteScrollRoot() {
    return document.querySelector('.collection[data-infinite-scroll]');
  }

  function buildPageUrl(path, queryString, pageNum) {
    const params = new URLSearchParams(queryString || '');
    params.set('page', String(pageNum));
    const qs = params.toString();
    return qs ? path + '?' + qs : path;
  }

  /** Page 1 strips `page` from the query; page 2+ sets `page` (filters preserved). */
  function buildCollectionUrlForPage(path, queryString, pageNum) {
    var params = new URLSearchParams(queryString || '');
    if (pageNum <= 1) {
      params.delete('page');
    } else {
      params.set('page', String(pageNum));
    }
    var qs = params.toString();
    return qs ? path + '?' + qs : path;
  }

  function findInfiniteScrollPageForProductLink(anchor, productList) {
    var el = anchor;
    while (el && el.parentElement !== productList) {
      if (!el.parentElement) return null;
      el = el.parentElement;
    }
    if (!el || el.parentElement !== productList) return null;
    var sib = el.previousElementSibling;
    while (sib) {
      if (sib.classList && sib.classList.contains('infinite-scroll-page-marker')) {
        var p = parseInt(sib.getAttribute('data-infinite-page') || '0', 10);
        return p > 0 ? p : null;
      }
      sib = sib.previousElementSibling;
    }
    return null;
  }

  /**
   * Remove each `.infinite-scroll-page-marker` with page < `productPage` and all following siblings
   * until the next page marker (pages strictly before the clicked product page).
   * Optional `scrollAnchorEl`: if still in DOM, scroll is adjusted so its viewport position is unchanged.
   */
  function removeInfiniteScrollContentBeforePage(productList, productPage, scrollAnchorEl) {
    if (productPage == null || productPage <= 1) return;
    var anchorTopBefore = null;
    if (scrollAnchorEl && scrollAnchorEl.isConnected) {
      anchorTopBefore = scrollAnchorEl.getBoundingClientRect().top;
    }
    while (true) {
      var mark = productList.querySelector('.infinite-scroll-page-marker[data-infinite-page]');
      if (!mark) break;
      var pn = parseInt(mark.getAttribute('data-infinite-page'), 10);
      if (isNaN(pn) || pn >= productPage) break;
      var el = mark;
      while (true) {
        var next = el.nextElementSibling;
        el.remove();
        if (!next || (next.classList && next.classList.contains('infinite-scroll-page-marker'))) break;
        el = next;
      }
    }
    void productList.offsetHeight;
    if (anchorTopBefore != null && scrollAnchorEl && scrollAnchorEl.isConnected) {
      var delta = scrollAnchorEl.getBoundingClientRect().top - anchorTopBefore;
      if (delta !== 0) {
        window.scrollBy(0, delta);
      }
    }
  }

  function getInfinitePageRange(productList) {
    var markers = productList.querySelectorAll('.infinite-scroll-page-marker[data-infinite-page]');
    var minV = Infinity;
    var maxV = 0;
    for (var i = 0; i < markers.length; i++) {
      var p = parseInt(markers[i].getAttribute('data-infinite-page'), 10);
      if (p > 0) {
        if (p < minV) minV = p;
        if (p > maxV) maxV = p;
      }
    }
    return { min: minV === Infinity ? 1 : minV, max: maxV || 1 };
  }

  function parseFetchedCollectionProductList(html) {
    if (!html) return null;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var pl = doc.querySelector('.collection .product-list');
    return pl && pl.children.length ? pl : null;
  }

  /** Back from PDP (?page>1): hide top loader, then restore history.scrollRestoration (init already used manual). */
  function doLazyRestore(collection, scrollRestorationBeforeManual) {
    return new Promise(function (resolve) {
      var loaderTop = collection.querySelector('.infinite-scroll-loader-top');
      if (loaderTop) loaderTop.setAttribute('hidden', '');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (scrollRestorationBeforeManual != null && typeof history !== 'undefined' && 'scrollRestoration' in history) {
            history.scrollRestoration = scrollRestorationBeforeManual;
          }
          resolve();
        });
      });
    });
  }

  function init() {
    if (typeof window.__dancoverCollectionPrependScrollCleanup === 'function') {
      try {
        window.__dancoverCollectionPrependScrollCleanup();
      } catch (e) {}
      window.__dancoverCollectionPrependScrollCleanup = null;
    }

    var collection = getCollectionInfiniteScrollRoot();
    if (!collection) return;

    var productList = collection.querySelector('.product-list');
    var sentinel = collection.querySelector('.infinite-scroll-sentinel');
    var loader = collection.querySelector('.infinite-scroll-loader');
    if (!productList || !sentinel || !loader) return;

    var currentPage = parseInt(collection.dataset.currentPage || '1', 10);
    var totalPages = parseInt(collection.dataset.totalPages || '1', 10);
    var path = collection.dataset.requestPath || '';
    var loading = false;
    var nextPageToLoad = currentPage + 1;
    var bottomLoadsRemain = nextPageToLoad <= totalPages;

    if (!bottomLoadsRemain && currentPage <= 1) return;

    collection.classList.add('infinite-scroll-active');

    var savedHistoryScrollRestoration = null;
    if (typeof history !== 'undefined' && 'scrollRestoration' in history) {
      savedHistoryScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
    }

    function restoreHistoryAfterCollectionPage1() {
      if (savedHistoryScrollRestoration !== null && typeof history !== 'undefined' && 'scrollRestoration' in history) {
        history.scrollRestoration = savedHistoryScrollRestoration;
        savedHistoryScrollRestoration = null;
      }
    }

    function getCurrentQueryString() {
      return (window.location.search || '').replace(/^\?/, '');
    }

    function updateBrowserUrl(pageNum) {
      var qs = getCurrentQueryString();
      var url = buildCollectionUrlForPage(path, qs, pageNum);
      if (window.location.pathname + (window.location.search || '') === url) return;
      window.history.replaceState({ page: pageNum }, '', url);
    }

    var urlObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.intersectionRatio < 0.1) return;
          var page = parseInt(entry.target.dataset.infinitePage || '0', 10);
          if (page) updateBrowserUrl(page);
        });
      },
      { root: null, rootMargin: '0px 0px -50% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    var loadingTopPrepend = false;
    var infiniteScrollWindowScrollAttached = false;
    var prependScrollLastY = 0;
    var appendScrollLastY = 0;

    function runRestOfInit() {
      collection.querySelectorAll('.infinite-scroll-page-marker').forEach(function (marker) {
        urlObserver.observe(marker);
      });

      var prependSentinel = collection.querySelector('.infinite-scroll-prepend-sentinel');
      var loaderTop = collection.querySelector('.infinite-scroll-loader-top');

      function rectIntersectsViewport(el) {
        if (!el) return false;
        var r = el.getBoundingClientRect();
        var vw = window.innerWidth || 0;
        var vh = window.innerHeight || 0;
        return r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw;
      }

      /** Sentinel sits directly above `.infinite-scroll-loader-top` (same scroll band; loader is hidden until fetch). */
      function isPrependTriggerZoneVisible() {
        if (loaderTop && !loaderTop.hasAttribute('hidden') && rectIntersectsViewport(loaderTop)) return true;
        if (prependSentinel && !prependSentinel.hasAttribute('hidden') && rectIntersectsViewport(prependSentinel)) return true;
        return false;
      }

      /** Bottom `.infinite-scroll-loader` is hidden until fetch; `.infinite-scroll-sentinel` sits just above it. */
      function isBottomAppendTriggerVisible() {
        if (loader && loader.isConnected && !loader.hasAttribute('hidden') && rectIntersectsViewport(loader)) return true;
        if (sentinel && sentinel.isConnected && rectIntersectsViewport(sentinel)) return true;
        return false;
      }

      function runPrependPreviousPage() {
        if (loadingTopPrepend) return;
        var minP = getInfinitePageRange(productList).min;
        if (minP <= 1) return;
        var pageToFetch = minP - 1;
        loadingTopPrepend = true;
        if (loaderTop) loaderTop.removeAttribute('hidden');
        setTimeout(function () {
          var currentQs = getCurrentQueryString();
          var url = buildPageUrl(path, currentQs, pageToFetch);
          fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
            .then(function (res) {
              return res.ok ? res.text() : null;
            })
            .then(function (html) {
              var responseProductList = parseFetchedCollectionProductList(html);
              if (!responseProductList) return;
              var ch = responseProductList.children;
              for (var c = ch.length - 1; c >= 0; c--) {
                productList.insertBefore(ch[c], productList.firstChild);
              }
              if (typeof window.__syncProductCardShippingRows === 'function') {
                window.__syncProductCardShippingRows();
              }
              productList.querySelectorAll('.infinite-scroll-page-marker').forEach(function (m) {
                urlObserver.observe(m);
              });
              updateBrowserUrl(pageToFetch);
            })
            .catch(function () {})
            .finally(function () {
              loadingTopPrepend = false;
              if (loaderTop) loaderTop.setAttribute('hidden', '');
              syncPrependSentinel();
              prependScrollLastY = window.scrollY;
              appendScrollLastY = window.scrollY;
            });
        }, LOADING_DELAY_MS);
      }

      function runAppendNextPage() {
        if (loading || nextPageToLoad > totalPages) return;
        if (!sentinel || !sentinel.isConnected || !loader || !loader.isConnected) return;
        loading = true;
        loader.hidden = false;
        setTimeout(function () {
          var currentQs = getCurrentQueryString();
          var pageFetched = nextPageToLoad;
          var nextUrl = buildPageUrl(path, currentQs, pageFetched);
          fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
            .then(function (res) {
              if (!res.ok) return null;
              return res.text();
            })
            .then(function (html) {
              var responseProductList = parseFetchedCollectionProductList(html);
              if (!responseProductList) {
                nextPageToLoad = totalPages + 1;
                return;
              }
              while (responseProductList.firstChild) {
                productList.appendChild(responseProductList.firstChild);
              }
              if (typeof window.__syncProductCardShippingRows === 'function') {
                window.__syncProductCardShippingRows();
              }
              nextPageToLoad = pageFetched + 1;
              var newMarkers = productList.querySelectorAll('.infinite-scroll-page-marker');
              newMarkers.forEach(function (m) { urlObserver.observe(m); });
              if (nextPageToLoad <= totalPages) {
                updateBrowserUrl(pageFetched);
              }
            })
            .catch(function () {
              nextPageToLoad = totalPages + 1;
            })
            .finally(function () {
              loading = false;
              loader.hidden = true;
              appendScrollLastY = window.scrollY;
              if (nextPageToLoad > totalPages) {
                sentinel.remove();
                loader.remove();
              }
            });
        }, LOADING_DELAY_MS);
      }

      function handleInfiniteScrollWindowScroll() {
        if (!collection.isConnected) return;
        var y = window.scrollY;
        var epsilon = 3;
        var scrolledUp = y + epsilon < prependScrollLastY;
        var scrolledDown = y > appendScrollLastY + epsilon;
        prependScrollLastY = y;
        appendScrollLastY = y;
        if (scrolledUp && !loadingTopPrepend && getInfinitePageRange(productList).min > 1 && isPrependTriggerZoneVisible()) {
          runPrependPreviousPage();
        }
        if (
          bottomLoadsRemain &&
          sentinel &&
          sentinel.isConnected &&
          scrolledDown &&
          !loading &&
          nextPageToLoad <= totalPages &&
          isBottomAppendTriggerVisible()
        ) {
          runAppendNextPage();
        }
      }

      /**
       * At scrollY === 0 the document cannot scroll further up, so `scroll` never fires with scrolledUp.
       * Wheel/trackpad "scroll up" (deltaY < 0) at the top still expresses intent to load the previous page.
       */
      function handleWheelAtTopForPrepend(e) {
        if (!collection.isConnected) return;
        if (e.deltaY >= 0) return;
        if (window.scrollY > 4) return;
        if (loadingTopPrepend) return;
        if (getInfinitePageRange(productList).min <= 1) return;
        if (!isPrependTriggerZoneVisible()) return;
        runPrependPreviousPage();
      }

      function ensureInfiniteScrollWindowScrollListener() {
        if (infiniteScrollWindowScrollAttached) return;
        infiniteScrollWindowScrollAttached = true;
        prependScrollLastY = window.scrollY;
        appendScrollLastY = window.scrollY;
        window.addEventListener('scroll', handleInfiniteScrollWindowScroll, { passive: true });
        window.addEventListener('wheel', handleWheelAtTopForPrepend, { passive: true });
        window.__dancoverCollectionPrependScrollCleanup = function () {
          window.removeEventListener('scroll', handleInfiniteScrollWindowScroll, { passive: true });
          window.removeEventListener('wheel', handleWheelAtTopForPrepend, { passive: true });
          infiniteScrollWindowScrollAttached = false;
        };
      }

      function syncPrependSentinel() {
        if (!prependSentinel) return;
        if (getInfinitePageRange(productList).min <= 1) {
          prependSentinel.setAttribute('hidden', '');
          if (loaderTop) loaderTop.setAttribute('hidden', '');
          return;
        }
        prependSentinel.removeAttribute('hidden');
      }

      syncPrependSentinel();
      ensureInfiniteScrollWindowScrollListener();

      collection.addEventListener('click', function (e) {
        var a = e.target.closest('a[href*="/products/"]');
        if (!a || !collection.contains(a)) return;
        if (e.defaultPrevented) return;
        if (e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if ((a.getAttribute('target') || '').toLowerCase() === '_blank') return;
        var productPage = findInfiniteScrollPageForProductLink(a, productList);
        if (productPage == null) return;
        e.preventDefault();
        removeInfiniteScrollContentBeforePage(productList, productPage, a);
        var qsForUrl = getCurrentQueryString();
        var targetUrl = buildCollectionUrlForPage(path, qsForUrl, productPage);
        var currentFull = window.location.pathname + (window.location.search || '');
        if (currentFull !== targetUrl) {
          window.history.replaceState({ page: productPage }, '', targetUrl);
        }
        var productHref = a.href;
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            window.location.assign(productHref);
          });
        });
      });

      if (!bottomLoadsRemain) {
        sentinel.remove();
        loader.remove();
      }
    }

    if (currentPage > 1) {
      doLazyRestore(collection, savedHistoryScrollRestoration).then(function () {
        savedHistoryScrollRestoration = null;
        nextPageToLoad = currentPage + 1;
        runRestOfInit();
      });
      return;
    }

    runRestOfInit();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        restoreHistoryAfterCollectionPage1();
      });
    });
  }

  function reinitAfterFacetUpdate(event) {
    var url = event.detail && event.detail.url;
    if (!url || !(url instanceof URL)) return;
    var sectionId = url.searchParams && url.searchParams.get('section_id');
    if (!sectionId) return;
    var section = document.getElementById('shopify-section-' + sectionId);
    if (!section) return;
    var mo = new MutationObserver(function () {
      if (section.querySelector('.collection[data-infinite-scroll]')) {
        init();
        mo.disconnect();
      }
    });
    mo.observe(section, { childList: true, subtree: true });
    setTimeout(function () { mo.disconnect(); }, 5000);
  }

  document.addEventListener('facet:update', reinitAfterFacetUpdate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
// End collection infinite scroll

// Swap .product-price-member-badge with its next sibling element (move it after the next element)
/*
function swapMemberBadgeWithNextSibling() {
  const badges = document.querySelectorAll('.product-price-member-badge');
  badges.forEach(function (badge) {
    // next *element* sibling (skips text nodes + comments)
    const nextEl = badge.nextElementSibling;
    if (!nextEl) return;
    // Move badge after the next element
    nextEl.insertAdjacentElement('afterend', badge);
  });
}
document.addEventListener('DOMContentLoaded', swapMemberBadgeWithNextSibling);
window.swapMemberBadgeWithNextSibling = swapMemberBadgeWithNextSibling;
*/

/* End Changes 2026/03/13 (ABU) */

// Shipping Module Web Component
// Bot UA pattern — duplicated from geo-location.js. Keep in sync if either copy is updated.
const SHIPPING_BOT_PATTERN = '(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)';
const SHIPPING_BOT_REGEX = new RegExp(SHIPPING_BOT_PATTERN, 'i');

function _isLikelyBot() {
  if (navigator.webdriver) return true;
  return SHIPPING_BOT_REGEX.test(navigator.userAgent);
}

class ShippingModule extends HTMLElement {
  static SHIPPING_RATES_ENDPOINT = '/apps/integration/shipping-rates';
  static UNCALCULATED_FREIGHT_NAMES = new Set([
    "ZIP",
    "SONDERZUSTELLUNG",
    "SPECIALE AFLEVERING",
    "LIVRAISON SPÉCIALE",
    "SPECIAL DELIVERY",
    "CONSEGNA PARTICOLARE",
    "SPECIEL LEVERING",
    "8",
    "ENTEGRA ESPECIAL",
    "ERIKOISLÄHETYS",
    "POSEBAN DOSTAVE",
    "SPECIALUS PRISTATYMAS",
    "IPAŠU PIEGADES",
    "DOSTAWA SPECJALNA",
    "TAXA DE ENVIO ESPECIAL",
    "SPECIALLEVERANS"
  ]);
  static GEO_API_URL = '/apps/integration/geo';
  static BROWSING_CONTEXT_URL = '/browsing_context_suggestions.json';
  static STORAGE_KEY = 'dtails:shipping-postal';
  static STORAGE_TTL_MS = 24 * 60 * 60 * 1000;
  static RATE_CACHE_PREFIX = 'dtails:shipping-rates:';
  static RATE_CACHE_TTL_MS = 30 * 60 * 1000;
  static DENMARK_TZ = 'Europe/Copenhagen';
  static DEFAULT_ZIPS = {
    AT: '8000', BE: '1000', BG: '1000', CH: '8000', CZ: '11000',
    DE: '10115', DK: '5000', EE: '10115', ES: '28001', FI: '00100',
    FR: '75000', GB: 'WC2N 4BD', GR: '10552', HR: '10000', HU: '1051',
    IE: 'D02 X285', IT: '00100', LT: '01121', LU: '1111', LV: 'LV-1050',
    MN: '14161', NL: '1022 AA', NO: '0010', PL: '00-950', PT: '1000-001',
    RO: '010203', SE: '111 21', SI: '1000', SK: '811 02'
  };

  static POSTAL_CODES = {
    AD:{p:/^(?:AD[1-7]0\d)$/i,e:'AD100'}, AF:{p:/^(?:\d{4})$/i,e:'1001'},
    AI:{p:/^(?:(?:AI-)?2640)$/i,e:'2640'}, AL:{p:/^(?:\d{4})$/i,e:'1001'},
    AM:{p:/^(?:(?:37)?\d{4})$/i,e:'375010'}, AR:{p:/^(?:(?:[A-HJ-NP-Z])?\d{4}(?:[A-Z]{3})?)$/i,e:'C1070AAM'},
    AS:{p:/^(?:96799(?:[ \-]\d{4})?)$/i,e:'96799'}, AT:{p:/^(?:\d{4})$/i,e:'1010'},
    AU:{p:/^(?:\d{4})$/i,e:'2060'}, AX:{p:/^(?:22\d{3})$/i,e:'22150'},
    AZ:{p:/^(?:\d{4})$/i,e:'1000'}, BA:{p:/^(?:\d{5})$/i,e:'71000'},
    BB:{p:/^(?:BB\d{5})$/i,e:'BB23026'}, BD:{p:/^(?:\d{4})$/i,e:'1340'},
    BE:{p:/^(?:\d{4})$/i,e:'4000'}, BG:{p:/^(?:\d{4})$/i,e:'1000'},
    BH:{p:/^(?:(?:1[0-2]|[1-9])\d{2})$/i,e:'317'}, BL:{p:/^(?:9[78][01]\d{2})$/i,e:'97100'},
    BM:{p:/^(?:[A-Z]{2} ?[A-Z0-9]{2})$/i,e:'FL 07'}, BN:{p:/^(?:[A-Z]{2} ?\d{4})$/i,e:'BT2328'},
    BR:{p:/^(?:\d{5}-?\d{3})$/i,e:'40301-110'}, BT:{p:/^(?:\d{5})$/i,e:'11001'},
    BY:{p:/^(?:\d{6})$/i,e:'223016'},
    CA:{p:/^(?:[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d)$/i,e:'H3Z 2Y7'},
    CC:{p:/^(?:6799)$/i,e:'6799'}, CH:{p:/^(?:\d{4})$/i,e:'2544'},
    CL:{p:/^(?:\d{7})$/i,e:'8340457'}, CN:{p:/^(?:\d{6})$/i,e:'266033'},
    CO:{p:/^(?:\d{6})$/i,e:'111221'}, CR:{p:/^(?:\d{4,5}|\d{3}-\d{4})$/i,e:'1000'},
    CU:{p:/^(?:\d{5})$/i,e:'10700'}, CV:{p:/^(?:\d{4})$/i,e:'7600'},
    CX:{p:/^(?:6798)$/i,e:'6798'}, CY:{p:/^(?:\d{4})$/i,e:'2008'},
    CZ:{p:/^(?:\d{3} ?\d{2})$/i,e:'100 00'}, DE:{p:/^(?:\d{5})$/i,e:'26133'},
    DK:{p:/^(?:\d{4})$/i,e:'8660'}, DO:{p:/^(?:\d{5})$/i,e:'11903'},
    DZ:{p:/^(?:\d{5})$/i,e:'40304'}, EC:{p:/^(?:\d{6})$/i,e:'090105'},
    EE:{p:/^(?:\d{5})$/i,e:'69501'}, EG:{p:/^(?:\d{5})$/i,e:'12411'},
    EH:{p:/^(?:\d{5})$/i,e:'70000'}, ES:{p:/^(?:\d{5})$/i,e:'28039'},
    ET:{p:/^(?:\d{4})$/i,e:'1000'}, FI:{p:/^(?:\d{5})$/i,e:'00550'},
    FK:{p:/^(?:FIQQ 1ZZ)$/i,e:'FIQQ 1ZZ'}, FM:{p:/^(?:9694[1-4](?:[ \-]\d{4})?)$/i,e:'96941'},
    FO:{p:/^(?:\d{3})$/i,e:'100'}, FR:{p:/^(?:\d{2} ?\d{3})$/i,e:'33380'},
    GB:{p:/^(?:GIR ?0AA|(?:(?:AB|AL|B|BA|BB|BD|BF|BH|BL|BN|BR|BS|BT|BX|CA|CB|CF|CH|CM|CO|CR|CT|CV|CW|DA|DD|DE|DG|DH|DL|DN|DT|DY|E|EC|EH|EN|EX|FK|FY|G|GL|GY|GU|HA|HD|HG|HP|HR|HS|HU|HX|IG|IM|IP|IV|JE|KA|KT|KW|KY|L|LA|LD|LE|LL|LN|LS|LU|M|ME|MK|ML|N|NE|NG|NN|NP|NR|NW|OL|OX|PA|PE|PH|PL|PO|PR|RG|RH|RM|S|SA|SE|SG|SK|SL|SM|SN|SO|SP|SR|SS|ST|SW|SY|TA|TD|TF|TN|TQ|TR|TS|TW|UB|W|WA|WC|WD|WF|WN|WR|WS|WV|YO|ZE)(?:\d[\dA-Z]? ?\d[ABD-HJLN-UW-Z]{2}))|BFPO ?\d{1,4})$/i,e:'EC1Y 8SY'},
    GE:{p:/^(?:\d{4})$/i,e:'0101'}, GF:{p:/^(?:9[78]3\d{2})$/i,e:'97300'},
    GG:{p:/^(?:GY\d[\dA-Z]? ?\d[ABD-HJLN-UW-Z]{2})$/i,e:'GY1 1AA'},
    GI:{p:/^(?:GX11 1AA)$/i,e:'GX11 1AA'}, GL:{p:/^(?:39\d{2})$/i,e:'3900'},
    GN:{p:/^(?:\d{3})$/i,e:'001'}, GP:{p:/^(?:9[78][01]\d{2})$/i,e:'97100'},
    GR:{p:/^(?:\d{3} ?\d{2})$/i,e:'151 24'}, GS:{p:/^(?:SIQQ 1ZZ)$/i,e:'SIQQ 1ZZ'},
    GT:{p:/^(?:\d{5})$/i,e:'09001'},
    GU:{p:/^(?:969(?:[12]\d|3[12])(?:[ \-]\d{4})?)$/i,e:'96910'}, GW:{p:/^(?:\d{4})$/i,e:'1000'},
    HM:{p:/^(?:\d{4})$/i,e:'7050'}, HN:{p:/^(?:\d{5})$/i,e:'31301'},
    HR:{p:/^(?:\d{5})$/i,e:'10000'}, HT:{p:/^(?:\d{4})$/i,e:'6120'},
    HU:{p:/^(?:\d{4})$/i,e:'1037'}, ID:{p:/^(?:\d{5})$/i,e:'40115'},
    IE:{p:/^(?:[\dA-Z]{3} ?[\dA-Z]{4})$/i,e:'A65 F4E2'}, IL:{p:/^(?:\d{5}(?:\d{2})?)$/i,e:'9614303'},
    IM:{p:/^(?:IM\d[\dA-Z]? ?\d[ABD-HJLN-UW-Z]{2})$/i,e:'IM2 1AA'}, IN:{p:/^(?:\d{6})$/i,e:'110034'},
    IO:{p:/^(?:BBND 1ZZ)$/i,e:'BBND 1ZZ'}, IQ:{p:/^(?:\d{5})$/i,e:'31001'},
    IR:{p:/^(?:\d{5}-?\d{5})$/i,e:'11936-12345'}, IS:{p:/^(?:\d{3})$/i,e:'320'},
    IT:{p:/^(?:\d{5})$/i,e:'00144'}, JE:{p:/^(?:JE\d[\dA-Z]? ?\d[ABD-HJLN-UW-Z]{2})$/i,e:'JE1 1AA'},
    JO:{p:/^(?:\d{5})$/i,e:'11937'}, JP:{p:/^(?:\d{3}-?\d{4})$/i,e:'154-0023'},
    KE:{p:/^(?:\d{5})$/i,e:'20100'}, KG:{p:/^(?:\d{6})$/i,e:'720001'},
    KH:{p:/^(?:\d{5,6})$/i,e:'120101'}, KR:{p:/^(?:\d{5})$/i,e:'03051'},
    KW:{p:/^(?:\d{5})$/i,e:'54541'}, KY:{p:/^(?:KY\d-\d{4})$/i,e:'KY1-1100'},
    KZ:{p:/^(?:\d{6})$/i,e:'040900'}, LA:{p:/^(?:\d{5})$/i,e:'01160'},
    LB:{p:/^(?:\d{4}(?: ?\d{4})?)$/i,e:'2038 3054'}, LI:{p:/^(?:948[5-9]|949[0-8])$/i,e:'9496'},
    LK:{p:/^(?:\d{5})$/i,e:'20000'}, LR:{p:/^(?:\d{4})$/i,e:'1000'},
    LS:{p:/^(?:\d{3})$/i,e:'100'}, LT:{p:/^(?:\d{5})$/i,e:'04340'},
    LU:{p:/^(?:\d{4})$/i,e:'4750'}, LV:{p:/^(?:LV-\d{4})$/i,e:'LV-1073'},
    MA:{p:/^(?:\d{5})$/i,e:'53000'}, MC:{p:/^(?:980\d{2})$/i,e:'98000'},
    MD:{p:/^(?:\d{4})$/i,e:'2012'}, ME:{p:/^(?:8\d{4})$/i,e:'81257'},
    MF:{p:/^(?:9[78][01]\d{2})$/i,e:'97100'}, MG:{p:/^(?:\d{3})$/i,e:'501'},
    MH:{p:/^(?:969[67]\d(?:[ \-]\d{4})?)$/i,e:'96960'}, MK:{p:/^(?:\d{4})$/i,e:'1314'},
    MM:{p:/^(?:\d{5})$/i,e:'11181'}, MN:{p:/^(?:\d{5})$/i,e:'65030'},
    MP:{p:/^(?:9695[012](?:[ \-]\d{4})?)$/i,e:'96950'}, MQ:{p:/^(?:9[78]2\d{2})$/i,e:'97220'},
    MT:{p:/^(?:[A-Z]{3} ?\d{2,4})$/i,e:'NXR 01'}, MU:{p:/^(?:\d{3}(?:\d{2}|[A-Z]{2}\d{3}))$/i,e:'42602'},
    MV:{p:/^(?:\d{5})$/i,e:'20026'}, MX:{p:/^(?:\d{5})$/i,e:'02860'},
    MY:{p:/^(?:\d{5})$/i,e:'43000'}, MZ:{p:/^(?:\d{4})$/i,e:'1102'},
    NA:{p:/^(?:\d{5})$/i,e:'10001'}, NC:{p:/^(?:988\d{2})$/i,e:'98814'},
    NE:{p:/^(?:\d{4})$/i,e:'8001'}, NF:{p:/^(?:2899)$/i,e:'2899'},
    NG:{p:/^(?:\d{6})$/i,e:'930283'}, NI:{p:/^(?:\d{5})$/i,e:'52000'},
    NL:{p:/^(?:[1-9]\d{3} ?(?:[A-RT-Z][A-Z]|S[BCE-RT-Z]))$/i,e:'1234 AB'},
    NO:{p:/^(?:\d{4})$/i,e:'0025'}, NP:{p:/^(?:\d{5})$/i,e:'44601'},
    NZ:{p:/^(?:\d{4})$/i,e:'6001'}, OM:{p:/^(?:(?:PC )?\d{3})$/i,e:'133'},
    PE:{p:/^(?:(?:LIMA \d{1,2}|CALLAO 0?\d)|[0-2]\d{4})$/i,e:'LIMA 23'},
    PF:{p:/^(?:987\d{2})$/i,e:'98709'}, PG:{p:/^(?:\d{3})$/i,e:'111'},
    PH:{p:/^(?:\d{4})$/i,e:'1008'}, PK:{p:/^(?:\d{5})$/i,e:'44000'},
    PL:{p:/^(?:\d{2}-\d{3})$/i,e:'00-950'}, PM:{p:/^(?:9[78]5\d{2})$/i,e:'97500'},
    PN:{p:/^(?:PCRN 1ZZ)$/i,e:'PCRN 1ZZ'}, PR:{p:/^(?:00[679]\d{2}(?:[ \-]\d{4})?)$/i,e:'00930'},
    PT:{p:/^(?:\d{4}-\d{3})$/i,e:'2725-079'}, PW:{p:/^(?:969(?:39|40)(?:[ \-]\d{4})?)$/i,e:'96940'},
    PY:{p:/^(?:\d{4})$/i,e:'1536'}, RE:{p:/^(?:9[78]4\d{2})$/i,e:'97400'},
    RO:{p:/^(?:\d{6})$/i,e:'060274'}, RS:{p:/^(?:\d{5,6})$/i,e:'106314'},
    RU:{p:/^(?:\d{6})$/i,e:'247112'}, SA:{p:/^(?:\d{5})$/i,e:'11564'},
    SD:{p:/^(?:\d{5})$/i,e:'11042'}, SE:{p:/^(?:\d{3} ?\d{2})$/i,e:'11455'},
    SG:{p:/^(?:\d{6})$/i,e:'546080'}, SH:{p:/^(?:(?:ASCN|STHL) 1ZZ)$/i,e:'STHL 1ZZ'},
    SI:{p:/^(?:\d{4})$/i,e:'4000'}, SJ:{p:/^(?:\d{4})$/i,e:'9170'},
    SK:{p:/^(?:\d{3} ?\d{2})$/i,e:'010 01'}, SM:{p:/^(?:4789\d)$/i,e:'47890'},
    SN:{p:/^(?:\d{5})$/i,e:'12500'}, SO:{p:/^(?:[A-Z]{2} ?\d{5})$/i,e:'JH 09010'},
    SV:{p:/^(?:CP [1-3][1-7][0-2]\d)$/i,e:'CP 1101'}, SZ:{p:/^(?:[HLMS]\d{3})$/i,e:'H100'},
    TC:{p:/^(?:TKCA 1ZZ)$/i,e:'TKCA 1ZZ'}, TH:{p:/^(?:\d{5})$/i,e:'10150'},
    TJ:{p:/^(?:\d{6})$/i,e:'735450'}, TM:{p:/^(?:\d{6})$/i,e:'744000'},
    TN:{p:/^(?:\d{4})$/i,e:'1002'}, TR:{p:/^(?:\d{5})$/i,e:'01960'},
    TW:{p:/^(?:\d{3}(?:\d{2,3})?)$/i,e:'104'}, TZ:{p:/^(?:\d{4,5})$/i,e:'6090'},
    UA:{p:/^(?:\d{5})$/i,e:'15432'}, UM:{p:/^(?:96898)$/i,e:'96898'},
    US:{p:/^(?:\d{5}(?:[ \-]\d{4})?)$/i,e:'95014'}, UY:{p:/^(?:\d{5})$/i,e:'11600'},
    UZ:{p:/^(?:\d{6})$/i,e:'702100'}, VA:{p:/^(?:00120)$/i,e:'00120'},
    VC:{p:/^(?:VC\d{4})$/i,e:'VC0100'}, VE:{p:/^(?:\d{4})$/i,e:'1010'},
    VG:{p:/^(?:VG\d{4})$/i,e:'VG1110'}, VI:{p:/^(?:008(?:[0-4]\d|5[01])(?:[ \-]\d{4})?)$/i,e:'00802-1222'},
    VN:{p:/^(?:\d{5}\d?)$/i,e:'70010'}, WF:{p:/^(?:986\d{2})$/i,e:'98600'},
    YT:{p:/^(?:976\d{2})$/i,e:'97600'}, ZA:{p:/^(?:\d{4})$/i,e:'0083'},
    ZM:{p:/^(?:\d{5})$/i,e:'50100'}
  };

  connectedCallback() {
    if (_isLikelyBot()) {
      this._initBase();
      this._bindEvents();
      this._showDeliveryForm();
      this._removeLoading();
      return;
    }

    if (document.visibilityState && document.visibilityState !== 'visible') {
      this._onVisibilityChange = () => {
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        this._onVisibilityChange = null;
        if (this.isConnected) this._doInit();
      };
      document.addEventListener('visibilitychange', this._onVisibilityChange);
      return;
    }

    this._doInit();
  }

  _initBase() {
    this._abortController = new AbortController();
    this._timerInterval = null;
    this._isCartDrawer = this.dataset.layout === 'cart-drawer';
    this._deliveryCollapsed = this.querySelector('[data-delivery-collapsed]');
    this._deliveryExpanded = this.querySelector('[data-delivery-expanded]');
    this._deliveryText = this.querySelector('[data-delivery-text]');
    this._postalPill = this.querySelector('[data-postal-pill]');
    this._postalPillText = this.querySelector('[data-postal-pill-text]');
    this._shippingLine = this.querySelector('[data-shipping-line]');
    this._shippingText = this.querySelector('[data-shipping-text]');
    this._shippingTextExpanded = this.querySelector('[data-shipping-text-expanded]');
    this._fulfilmentLine = this.querySelector('[data-fulfilment-line]');
    this._fulfilmentText = this.querySelector('[data-fulfilment-text]');
    this._postalInput = this.querySelector('[data-postal-input]');
    this._postalSubmit = this.querySelector('[data-postal-submit]');
    this._countrySelect = this.querySelector('[data-country-select]');
    this._cartBar = this.querySelector('[data-cart-bar]');
    this._cartCollapsed = this.querySelector('[data-cart-collapsed]');
    this._cartExpanded = this.querySelector('[data-cart-expanded]');
    this._cartDestination = this.querySelector('[data-cart-destination]');
    this._cartEdit = this.querySelector('[data-cart-edit]');
    this._cartClose = this.querySelector('[data-cart-close]');
    this._isMultiCountry = this.dataset.multiCountry === 'true';
    this._availableCountries = (this.dataset.availableCountries || '').split(',').filter(Boolean);
    const freightScript = this.querySelector('script[data-freight-info]');
    try { this._freightInfo = freightScript ? JSON.parse(freightScript.textContent) : null; } catch(e) { this._freightInfo = null; }
  }

  _getCartVariantItems() {
    if (!this._isCartDrawer) return [];
    try {
      const parsed = JSON.parse(this.dataset.variantIds || '[]');
      return Array.isArray(parsed)
        ? parsed
            .filter(item => item?.variantId)
            .map(item => ({
              variantId: String(item.variantId),
              quantity: parseInt(item.quantity, 10) || 1
            }))
        : [];
    } catch (_) {
      return [];
    }
  }

  _getCartRateCacheKey(items, postalCode, countryCode) {
    const itemsKey = items
      .map(item => `${item.variantId}:${item.quantity}`)
      .sort()
      .join(',');
    return `${ShippingModule.RATE_CACHE_PREFIX}cart:${itemsKey}:${postalCode}:${countryCode}`;
  }

  _getRateVariantContext() {
    if (this._isCartDrawer) {
      const items = this._getCartVariantItems();
      return items.length ? { items } : null;
    }

    const variantId = this.dataset.variantId;
    const productId = this.dataset.productId;
    if (!variantId || !productId) return null;

    let quantity = 1;
    const qtyInput = document.querySelector('quantity-selector input[name="quantity"]');
    if (qtyInput?.value) quantity = parseInt(qtyInput.value, 10) || 1;

    return { variantId, quantity, productId };
  }

  _doInit() {
    this._initBase();
    if (!this._isCartDrawer) this._startCountdown();
    this._bindEvents();
    this._deferUntilVisible();
  }

  _deferUntilVisible() {
    if (!('IntersectionObserver' in window)) {
      this._initPostalCode().finally(() => this._removeLoading());
      return;
    }

    this._intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this._intersectionObserver.disconnect();
        this._intersectionObserver = null;
        this._initPostalCode().finally(() => this._removeLoading());
      }
    }, { threshold: 0 });

    this._intersectionObserver.observe(this);
  }

  _removeLoading() {
    this.classList.remove('shipping-module--loading');
    if (this._isCartDrawer && this._cartBar) {
      this._cartBar.removeAttribute('hidden');
    }
  }

  disconnectedCallback() {
    if (this._abortController) this._abortController.abort();
    if (this._timerInterval) clearInterval(this._timerInterval);
    if (this._intersectionObserver) this._intersectionObserver.disconnect();
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      this._onVisibilityChange = null;
    }
  }

  _bindEvents() {
    const signal = this._abortController.signal;

    if (this._postalSubmit) {
      this._postalSubmit.addEventListener('click', () => this._handlePostalSubmit(), { signal });
    }

    if (this._postalInput) {
      this._postalInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this._handlePostalSubmit();
        }
      }, { signal });
      this._postalInput.addEventListener('input', () => {
        this._postalInput.classList.remove('input--error');
        this._postalInput.removeAttribute('aria-invalid');
        this._hidePostalHint();
      }, { signal });
    }

    if (this._postalPill) {
      this._postalPill.addEventListener('click', () => this._showDeliveryForm(), { signal });
    }

    if (this._cartEdit) {
      this._cartEdit.addEventListener('click', () => this._showDeliveryForm(), { signal });
    }

    if (this._cartClose) {
      this._cartClose.addEventListener('click', () => this._collapseCartBar(), { signal });
    }

    if (this._countrySelect) {
      this._countrySelect.addEventListener('change', () => {
        const code = this._getSelectedCountryCode();
        const fallbackZip = ShippingModule.DEFAULT_ZIPS[code?.toUpperCase()] || '';
        if (this._postalInput) this._postalInput.value = fallbackZip;
      }, { signal });
    }

    document.addEventListener('variant:change', (e) => {
      const newVariantId = this.dataset.variantId;
      if (newVariantId) this._refetchRatesIfNeeded();
    }, { signal });

    if (this._isCartDrawer) {
      document.addEventListener('cart:change', () => {
        if (this.isConnected) this._refetchRatesIfNeeded();
      }, { signal });
    }
  }

  async _initPostalCode() {
    const stored = this._getStoredPostal();

    if (stored && stored.postalCode && this._isCountryAvailable(stored.countryCode)) {
      this._preselectCountry(stored.countryCode);
      const countryName = stored.countryName || this._getCountryNameByCode(stored.countryCode);
      return this._applyPostalCode(stored.postalCode, stored.city || '', stored.countryCode || this.dataset.countryCode, countryName);
    }

    if (stored) {
      const cachedCountry = this._isCountryAvailable(stored.countryCode) ? stored.countryCode : null;
      return this._applyFallback(cachedCountry);
    }

    let detectedCountryCode = null;

    try {
      const geo = await this._detectLocation();
      if (geo) {
        this._storePostal(geo.postalCode || '', geo.city || '', geo.countryCode || '', geo.countryName || '');
      }
      if (geo && geo.postalCode && this._isCountryAvailable(geo.countryCode)) {
        this._preselectCountry(geo.countryCode);
        const countryName = geo.countryName || this._getCountryNameByCode(geo.countryCode);
        return this._applyPostalCode(geo.postalCode, geo.city, geo.countryCode, countryName);
      }
      if (geo && geo.countryCode && this._isCountryAvailable(geo.countryCode)) {
        detectedCountryCode = geo.countryCode;
        this._preselectCountry(geo.countryCode);
      }
    } catch (_) {
      // Geo detection failed
    }

    return this._applyFallback(detectedCountryCode);
  }

  _applyFallback(detectedCountryCode) {
    const countryCode = detectedCountryCode || this.dataset.countryCode;
    this._preselectCountry(countryCode);

    const fallbackZip = ShippingModule.DEFAULT_ZIPS[countryCode?.toUpperCase()];
    if (fallbackZip) {
      const countryName = this._getCountryNameByCode(countryCode) || this.dataset.countryName;
      return this._applyPostalCode(fallbackZip, '', countryCode, countryName);
    }

    this._showDeliveryForm();
  }

  _isCountryAvailable(countryCode) {
    if (!countryCode) return false;
    if (this._availableCountries.length === 0) return true;
    return this._availableCountries.includes(countryCode.toUpperCase());
  }

  _preselectCountry(countryCode) {
    if (!this._countrySelect || !countryCode) return;
    const upper = countryCode.toUpperCase();
    for (const opt of this._countrySelect.options) {
      if (opt.dataset.code === upper) {
        this._countrySelect.value = opt.value;
        this._countrySelect.dispatchEvent(new Event('change'));
        break;
      }
    }
  }

  _getCountryNameByCode(countryCode) {
    if (!this._countrySelect || !countryCode) return '';
    const upper = countryCode.toUpperCase();
    for (const opt of this._countrySelect.options) {
      if (opt.dataset.code === upper) return opt.value;
    }
    return '';
  }

  async _detectLocation() {
    try {
      const response = await fetch(ShippingModule.GEO_API_URL, { signal: this._abortController.signal });
      if (response.ok) {
        const data = await response.json();
        if (data.zip) {
          return {
            postalCode: data.zip,
            city: data.city || '',
            countryCode: data.country_code || this.dataset.countryCode,
            countryName: data.country_name || ''
          };
        }
      }
    } catch (_) {
      // Primary geo failed
    }

    try {
      const response = await fetch(
        (window.Shopify?.routes?.root || '/') + 'browsing_context_suggestions.json',
        { signal: this._abortController.signal }
      );
      if (response.ok) {
        const data = await response.json();
        const country = data?.detected_values?.country;
        if (country?.handle) {
          return { postalCode: '', city: '', countryCode: country.handle.toUpperCase() };
        }
      }
    } catch (_) {
      // Fallback also failed
    }

    return null;
  }

  _handlePostalSubmit() {
    const postal = this._postalInput?.value?.trim();
    if (!postal) return;

    const countryCode = this._getSelectedCountryCode();
    if (!this._validatePostalCode(postal, countryCode)) return;

    const countryName = this._getSelectedCountryName();
    this._storePostal(postal, '', countryCode, countryName);
    this.classList.add('shipping-module--loading');
    this._applyPostalCode(postal, '', countryCode, countryName)
      .finally(() => this._removeLoading());
  }

  _validatePostalCode(postal, countryCode) {
    const entry = ShippingModule.POSTAL_CODES[countryCode];
    if (!entry) return true;
    const valid = entry.p.test(postal);
    if (!valid && this._postalInput) {
      this._postalInput.classList.add('input--error');
      this._postalInput.setAttribute('aria-invalid', 'true');
      this._showPostalHint(entry.e);
    } else if (this._postalInput) {
      this._postalInput.classList.remove('input--error');
      this._postalInput.removeAttribute('aria-invalid');
      this._hidePostalHint();
    }
    return valid;
  }

  _showPostalHint(example) {
    let hint = this.querySelector('[data-postal-hint]');
    if (!hint) {
      hint = document.createElement('span');
      hint.setAttribute('data-postal-hint', '');
      hint.className = 'shipping-module__postal-hint';
      const row = this._postalInput?.closest('.shipping-module__postal-input-row')
        || this._postalInput?.closest('.shipping-module__cart-expanded-form')
        || this._postalInput?.parentElement;
      if (row) row.insertAdjacentElement('afterend', hint);
    }
    hint.textContent = example ? `${this._t('shippingZipFormat')}: ${example}` : this._t('shippingZipInvalid');
    hint.hidden = false;
  }

  _hidePostalHint() {
    const hint = this.querySelector('[data-postal-hint]');
    if (hint) hint.hidden = true;
  }

  _getSelectedCountryCode() {
    if (this._countrySelect) {
      const opt = this._countrySelect.options[this._countrySelect.selectedIndex];
      return opt?.dataset?.code || this.dataset.countryCode;
    }
    return this.dataset.countryCode;
  }

  _getSelectedCountryName() {
    if (this._countrySelect) {
      return this._countrySelect.value || '';
    }
    return '';
  }

  _applyPostalCode(postalCode, city, countryCode, countryName) {
    if (this._postalPillText) {
      const code = (countryCode || this.dataset.countryCode || '').toUpperCase();
      const label = city ? `${postalCode}, ${city}` : postalCode;
      this._postalPillText.textContent = code ? `${label}, ${code}` : label;
    }

    if (this._isCartDrawer) {
      this._updateCartDestination(postalCode, countryCode);
      this._collapseCartBar();
    } else {
      if (this._deliveryText) {
        this._deliveryText.textContent = this._t('shippingDeliveryTo');
      }

      if (this._deliveryCollapsed) this._deliveryCollapsed.removeAttribute('hidden');
      if (this._deliveryExpanded) this._deliveryExpanded.setAttribute('hidden', '');
    }

    if (this._postalInput) this._postalInput.value = postalCode;

    if (!this._isCartDrawer) this._updateFreightData(countryCode || this.dataset.countryCode);

    return this._fetchShippingRates(postalCode, countryCode, countryName);
  }

  _updateCartDestination(postalCode, countryCode) {
    if (!this._cartDestination) return;
    const code = (countryCode || this.dataset.countryCode || '').toUpperCase();
    this._cartDestination.textContent = code ? `${code} ${postalCode}` : postalCode;
  }

  _collapseCartBar() {
    if (!this._isCartDrawer) return;
    if (this._cartCollapsed) this._cartCollapsed.removeAttribute('hidden');
    if (this._cartExpanded) this._cartExpanded.setAttribute('hidden', '');
  }

  _updateFreightData(countryCode) {
    if (!this._freightInfo || !countryCode) return;
    const data = this._freightInfo[countryCode];
    if (data) {
      this.dataset.showMode = data.showMode || '';
      if (data.showMode === 'Days') {
        this.dataset.minDays = data.deliveryTime?.minDays || 0;
        this.dataset.maxDays = data.deliveryTime?.maxDays || 0;
        this.dataset.deliveryDate = '';
      } else if (data.showMode === 'Date') {
        this.dataset.deliveryDate = data.deliveryDate || '';
        this.dataset.minDays = 0;
        this.dataset.maxDays = 0;
      } else {
        this.dataset.showMode = '';
        this.dataset.minDays = 0;
        this.dataset.maxDays = 0;
        this.dataset.deliveryDate = '';
      }
    } else {
      this.dataset.showMode = '';
      this.dataset.minDays = 0;
      this.dataset.maxDays = 0;
      this.dataset.deliveryDate = '';
    }
  }

  _showDeliveryForm() {
    if (this._isCartDrawer) {
      if (this._cartBar) this._cartBar.removeAttribute('hidden');
      if (this._cartCollapsed) this._cartCollapsed.setAttribute('hidden', '');
      if (this._cartExpanded) this._cartExpanded.removeAttribute('hidden');
      if (this._postalInput) this._postalInput.focus();
      return;
    }

    if (this._deliveryCollapsed) this._deliveryCollapsed.setAttribute('hidden', '');
    if (this._deliveryExpanded) this._deliveryExpanded.removeAttribute('hidden');
    if (this._shippingLine) this._shippingLine.setAttribute('hidden', '');
    if (this._postalInput) this._postalInput.focus();
  }

  async _fetchShippingRates(postalCode, countryCode, countryName) {
    const rateContext = this._getRateVariantContext();
    if (!rateContext) return;

    if (this._postalSubmit) this._postalSubmit.setAttribute('aria-busy', 'true');

    try {
      if (this._isCartDrawer) {
        const { items } = rateContext;
        const cacheKey = this._getCartRateCacheKey(items, postalCode, countryCode);
        const cached = this._getCachedRatesByKey(cacheKey);
        if (cached) {
          this._displayShippingRates(cached);
          return;
        }

        const body = JSON.stringify({
          items,
          currency: this.dataset.currency || window.Shopify?.currency?.active,
          locale: this.dataset.locale || window.Shopify?.locale,
          shipping_address: {
            country_code: countryCode || this.dataset.countryCode,
            zip: postalCode
          }
        });

        const response = await fetch(ShippingModule.SHIPPING_RATES_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          signal: this._abortController.signal
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();

        if (data.shipping_rates && data.shipping_rates.length > 0) {
          this._setCachedRatesByKey(cacheKey, data.shipping_rates);
          this._displayShippingRates(data.shipping_rates);
        }
        return;
      }

      const { variantId, quantity } = rateContext;

      const cached = this._getCachedRates(variantId, postalCode, countryCode, quantity);
      if (cached) {
        this._displayShippingRates(cached);
        return;
      }

      const country = countryName || this._getSelectedCountryName() || this.dataset.countryName || countryCode || this.dataset.countryCode;

      const body = JSON.stringify({
        variantId: variantId,
        quantity: quantity,
        currency: this.dataset.currency || window.Shopify?.currency?.active,
        locale: this.dataset.locale || window.Shopify?.locale,
        shipping_address: {
          zip: postalCode,
          country: country,
          country_code: countryCode || this.dataset.countryCode
        }
      });

      const response = await fetch(ShippingModule.SHIPPING_RATES_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: this._abortController.signal
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();

      if (data.shipping_rates && data.shipping_rates.length > 0) {
        this._setCachedRates(variantId, postalCode, countryCode, quantity, data.shipping_rates);
        this._displayShippingRates(data.shipping_rates);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Shipping module rate fetch error:', error);
      }
    } finally {
      if (this._postalSubmit) this._postalSubmit.removeAttribute('aria-busy');
    }
  }

  _displayShippingRates(rates) {
    const hasUncalculatedFreight = rates.some((rate) => {
      const rateName = rate.presentment_name || rate.name || '';
      const normalizedRateName = rateName.normalize('NFKC').trim().toLocaleUpperCase();
      return ShippingModule.UNCALCULATED_FREIGHT_NAMES.has(normalizedRateName);
    });
    const infoButton = this._shippingLine?.querySelector('[data-tooltip-target]');
    const tooltipId = infoButton?.getAttribute('data-tooltip-target');
    const tooltip = tooltipId ? document.getElementById(tooltipId) : null;

    if (hasUncalculatedFreight) {
      if (this._shippingText) this._shippingText.textContent = this._t('shippingEstimatorCalculationFailed');
      if (this._shippingLine) {
        this._shippingLine.classList.remove('tooltip-active');
        this._shippingLine.removeAttribute('hidden');
      }
      if (infoButton) infoButton.setAttribute('hidden', '');
      if (tooltip) tooltip.setAttribute('hidden', '');
      return;
    }

    if (infoButton) infoButton.removeAttribute('hidden');

    const deliveryRates = rates.filter(r => {
      const name = (r.presentment_name || r.name || '').toLowerCase();
      return !name.includes('dancover');
    });
    const sorted = [...(deliveryRates.length ? deliveryRates : rates)].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    const displayRate = sorted[0];
    const currency = displayRate.currency || this.dataset.currency;
    const priceValue = parseFloat(displayRate.price);
    const isFree = priceValue === 0;
    // Intentionally omit `locale` so formatMoney uses the canonical market locale
    // from window.themeVariables.locale (e.g. "nb-NO"). data-locale here is the
    // language only (request.locale.iso_code, e.g. "nb"), which would bypass
    // SYMBOL_POSITION_OVERRIDE and country-native formatting, diverging from SSR
    // and other PDP prices.
    const priceLabel = window.theme.formatMoney(priceValue, {
      currency,
      intlOptions: { minimumFractionDigits: 0, maximumFractionDigits: 0 },
    });

    const text = this._buildShippingText(isFree ? null : priceLabel);

    if (this._isCartDrawer) {
      if (this._shippingText) this._shippingText.textContent = text;
      if (this._shippingTextExpanded) this._shippingTextExpanded.textContent = text;
      if (this._cartBar) this._cartBar.removeAttribute('hidden');
      this._collapseCartBar();
      return;
    }

    if (this._shippingText) this._shippingText.textContent = text;
    if (this._shippingLine) this._shippingLine.removeAttribute('hidden');
  }
  _buildShippingText(priceLabel) {
    if (this._isCartDrawer) {
      return priceLabel === null
        ? this._t('shippingFromFree')
        : this._t('shippingFrom', { price: priceLabel });
    }

    const free = priceLabel === null;
    const suffix = free ? 'Free' : '';
    const showMode = this.dataset.showMode;

    if (showMode === 'Days') {
      const minDays = parseInt(this.dataset.minDays) || 0;
      const maxDays = parseInt(this.dataset.maxDays) || 0;
      if (minDays > 0 || maxDays > 0) {
        if (minDays === maxDays || minDays === 0 || maxDays === 0) {
          const days = maxDays || minDays;
          return this._t('shippingLineDaysSingle' + suffix, { days, price: priceLabel });
        }
        return this._t('shippingLineDaysRange' + suffix, { min: minDays, max: maxDays, price: priceLabel });
      }
    }

    if (showMode === 'Date') {
      const dateStr = this.dataset.deliveryDate;
      if (dateStr) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          const locale = this.dataset.locale || 'en';
          const formatted = date.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
          return this._t('shippingLineDate' + suffix, { date: formatted, price: priceLabel });
        }
      }
    }

    return this._t('shippingLineNoDate' + suffix, { price: priceLabel });
  }

  // Fulfilment countdown to cutoff hour in Danish time
  _startCountdown() {
    const cutoffHour = parseInt(this.dataset.cutoffHour) || 15;
    this._updateCountdown(cutoffHour);
    this._timerInterval = setInterval(() => this._updateCountdown(cutoffHour), 1000);
  }

  _updateCountdown(cutoffHour) {
    const now = new Date();
    const dkDateStr = now.toLocaleString('en-GB', {
      timeZone: ShippingModule.DENMARK_TZ,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    const parts = dkDateStr.match(/(\d{2})\/(\d{2})\/(\d{4}),?\s*(\d{2}):(\d{2}):(\d{2})/);
    if (!parts) return;

    const [, dd, mm, yyyy, hh, mi, ss] = parts.map(Number);
    const dkNow = new Date(yyyy, mm - 1, dd, hh, mi, ss);
    const dayOfWeek = dkNow.getDay();
    const currentSeconds = hh * 3600 + mi * 60 + ss;
    const cutoffSeconds = cutoffHour * 3600;
    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
    const beforeCutoff = currentSeconds < cutoffSeconds;

    if (isWeekday && beforeCutoff) {
      const remaining = cutoffSeconds - currentSeconds;
      const h = Math.floor(remaining / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = remaining % 60;
      const countdown = `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;

      if (this._fulfilmentText) {
        this._fulfilmentText.textContent = this._t('shippingFulfilmentToday', { countdown });
      }
    } else {
      let daysUntilNextBusiness = 1;
      if (dayOfWeek === 5 && !beforeCutoff) daysUntilNextBusiness = 3;
      else if (dayOfWeek === 6) daysUntilNextBusiness = 2;
      else if (dayOfWeek === 0) daysUntilNextBusiness = 1;

      const remainingToday = 86400 - currentSeconds;
      const totalRemaining = remainingToday + (daysUntilNextBusiness - 1) * 86400 + cutoffSeconds;

      const h = Math.floor(totalRemaining / 3600);
      const m = Math.floor((totalRemaining % 3600) / 60);
      const s = totalRemaining % 60;
      const countdown = `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;

      if (this._fulfilmentText) {
        this._fulfilmentText.textContent = this._t('shippingFulfilmentOrderWithin', { countdown });
      }
    }
  }

  _refetchRatesIfNeeded() {
    const stored = this._getStoredPostal();
    if (stored?.postalCode) {
      const countryName = stored.countryName || this._getCountryNameByCode(stored.countryCode) || this._getSelectedCountryName();
      this._fetchShippingRates(stored.postalCode, stored.countryCode || this.dataset.countryCode, countryName);
    }
  }

  _t(key, vars) {
    let template = window.themeVariables?.strings?.[key] || key;
    const decoder = document.createElement('textarea');
    decoder.innerHTML = template;
    template = decoder.value;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        template = template.split(`__${k}__`).join(v);
      }
    }
    return template;
  }

  _getStoredPostal() {
    try {
      const raw = localStorage.getItem(ShippingModule.STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
        localStorage.removeItem(ShippingModule.STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch (_) {
      return null;
    }
  }

  _storePostal(postalCode, city, countryCode, countryName) {
    try {
      const expiresAt = Date.now() + ShippingModule.STORAGE_TTL_MS;
      localStorage.setItem(ShippingModule.STORAGE_KEY, JSON.stringify({ postalCode, city, countryCode, countryName, expiresAt }));
    } catch (_) {
      // localStorage unavailable
    }
  }

  _getCachedRatesByKey(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
        sessionStorage.removeItem(key);
        return null;
      }
      return parsed.rates;
    } catch (_) {
      return null;
    }
  }

  _setCachedRatesByKey(key, rates) {
    try {
      const expiresAt = Date.now() + ShippingModule.RATE_CACHE_TTL_MS;
      sessionStorage.setItem(key, JSON.stringify({ rates, expiresAt }));
    } catch (_) {
      // sessionStorage unavailable
    }
  }

  _getRateCacheKey(variantId, postalCode, countryCode, quantity) {
    return `${ShippingModule.RATE_CACHE_PREFIX}${variantId}:${postalCode}:${countryCode}:${quantity}`;
  }

  _getCachedRates(variantId, postalCode, countryCode, quantity) {
    try {
      const key = this._getRateCacheKey(variantId, postalCode, countryCode, quantity);
      return this._getCachedRatesByKey(key);
    } catch (_) {
      return null;
    }
  }

  _setCachedRates(variantId, postalCode, countryCode, quantity, rates) {
    try {
      const key = this._getRateCacheKey(variantId, postalCode, countryCode, quantity);
      this._setCachedRatesByKey(key, rates);
    } catch (_) {
      // sessionStorage unavailable
    }
  }
}

if (!window.customElements.get('shipping-module')) {
  window.customElements.define('shipping-module', ShippingModule);
}

/* Changes 2026/05/06 (ABU) */
/*
  PDP complementary-product carousel:
  the native arrow navigation stops on the last card offset, not on the real
  scroll end. Because the PDP desktop track has extra end clearance for the
  fade overlay, the last card can still look clipped after the final next click.
  When the carousel settles on the last page, force it to the true max scroll.
*/
(function () {
  var desktopMedia = window.matchMedia('(min-width: 700px)');

  function syncPdpCarouselEnd(carousel) {
    if (!carousel || carousel.dataset.dcpPdpCarouselEndFix === 'true') return;

    carousel.dataset.dcpPdpCarouselEndFix = 'true';

    function getEscapedCarouselId() {
      if (!carousel.id) return false;

      return window.CSS && typeof window.CSS.escape === 'function'
        ? window.CSS.escape(carousel.id)
        : carousel.id.replace(/["\\]/g, '\\$&');
    }

    function getNextButtons() {
      var escapedId = getEscapedCarouselId();
      if (!escapedId) return [];

      return document.querySelectorAll('button[aria-controls="' + escapedId + '"][is="next-button"]');
    }

    function isNextControlDisabled() {
      var nextButton = getNextButtons()[0];

      return Boolean(nextButton && (nextButton.disabled || nextButton.getAttribute('aria-disabled') === 'true'));
    }

    function snapLastPageToEnd() {
      if (!desktopMedia.matches) return;
      if (!carousel.offsetParent) return;

      var lastPageIndex = Math.max(0, carousel.totalPages - 1);
      var isLastPage = typeof carousel.totalPages === 'number'
        && typeof carousel.selectedIndex === 'number'
        && carousel.selectedIndex >= lastPageIndex;

      if (!isLastPage && !isNextControlDisabled()) return;

      var maxScrollLeft = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
      if (maxScrollLeft - carousel.scrollLeft <= 2) return;

      carousel.scrollTo({ left: maxScrollLeft, behavior: 'smooth' });
    }

    carousel.addEventListener('carousel:settle', snapLastPageToEnd);
    carousel.addEventListener('scrollend', snapLastPageToEnd);
    window.addEventListener('resize', snapLastPageToEnd);

    getNextButtons().forEach(function (button) {
      button.addEventListener('click', function () {
        window.setTimeout(snapLastPageToEnd, 260);
      });
    });
  }

  function initPdpCarouselEndFix(root) {
    (root || document).querySelectorAll('.product-info__complementary-products .horizontal-product-list-carousel').forEach(syncPdpCarouselEnd);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initPdpCarouselEndFix(document);
    });
  } else {
    initPdpCarouselEndFix(document);
  }

  var pdpCarouselObserver = new MutationObserver(function () {
    initPdpCarouselEndFix(document);
  });

  pdpCarouselObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();

/*
  PDP mobile carousel drag:
  native touch scrolling works on phones, but desktop mobile previews do not
  always turn a mouse drag into horizontal scroll. This keeps the PDP rail
  draggable at the mobile breakpoint without affecting vertical page scroll.
*/
(function () {
  var mobileMedia = window.matchMedia('(max-width: 699px)');
  var selector = '.product-info__complementary-products .horizontal-product-list-carousel';

  function isInteractiveElement(element) {
    return element.closest('button, input, select, textarea, label');
  }

  function enhancePdpMobileDrag(carousel) {
    if (!carousel || carousel.dataset.dcpPdpMobileDrag === 'true') return;

    carousel.dataset.dcpPdpMobileDrag = 'true';

    var isDragging = false;
    var isHorizontalDrag = false;
    var startX = 0;
    var startY = 0;
    var startScrollLeft = 0;
    var activePointerId = null;
    var suppressClick = false;

    function stopDragging(event) {
      if (!isDragging) return;

      isDragging = false;
      isHorizontalDrag = false;
      carousel.classList.remove('is-dcp-pdp-mobile-dragging');

      if (activePointerId !== null) {
        try {
          carousel.releasePointerCapture(activePointerId);
        } catch (_) {}
      }

      activePointerId = null;

      if (event && event.type !== 'pointercancel') {
        window.setTimeout(function () {
          suppressClick = false;
        }, 120);
      } else {
        suppressClick = false;
      }
    }

    carousel.addEventListener('pointerdown', function (event) {
      if (!mobileMedia.matches) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (isInteractiveElement(event.target)) return;

      isDragging = true;
      isHorizontalDrag = false;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = carousel.scrollLeft;
      activePointerId = event.pointerId;

      try {
        carousel.setPointerCapture(event.pointerId);
      } catch (_) {}
    });

    carousel.addEventListener('pointermove', function (event) {
      if (!isDragging || !mobileMedia.matches) return;

      var deltaX = event.clientX - startX;
      var deltaY = event.clientY - startY;
      var absX = Math.abs(deltaX);
      var absY = Math.abs(deltaY);

      if (!isHorizontalDrag) {
        if (absY > 8 && absY > absX * 1.15) {
          stopDragging(event);
          return;
        }

        if (absX < 6 || absX <= absY) {
          return;
        }

        isHorizontalDrag = true;
        suppressClick = true;
        carousel.classList.add('is-dcp-pdp-mobile-dragging');
      }

      event.preventDefault();
      carousel.scrollLeft = startScrollLeft - deltaX;
    }, { passive: false });

    carousel.addEventListener('pointerup', stopDragging);
    carousel.addEventListener('pointercancel', stopDragging);
    carousel.addEventListener('pointerleave', stopDragging);

    carousel.addEventListener('click', function (event) {
      if (!suppressClick) return;

      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, true);
  }

  function initPdpMobileDrag(root) {
    (root || document).querySelectorAll(selector).forEach(enhancePdpMobileDrag);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initPdpMobileDrag(document);
    });
  } else {
    initPdpMobileDrag(document);
  }

  var pdpMobileDragObserver = new MutationObserver(function () {
    initPdpMobileDrag(document);
  });

  pdpMobileDragObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
/* End Changes 2026/05/06 (ABU) */


/* Changes 2026/05/18 (ABU) */
(function () {
  var observedPandectesRoots = new WeakSet();

  function hidePandectesRenewButton(host) {
    if (!host || !host.shadowRoot) return;

    host.shadowRoot.querySelectorAll('button.widget').forEach(function (button) {
      button.style.setProperty('display', 'none', 'important');
      button.style.setProperty('visibility', 'hidden', 'important');
      button.style.setProperty('pointer-events', 'none', 'important');
    });

    if (!observedPandectesRoots.has(host.shadowRoot)) {
      observedPandectesRoots.add(host.shadowRoot);
      new MutationObserver(function () {
        hidePandectesRenewButton(host);
      }).observe(host.shadowRoot, {
        childList: true,
        subtree: true
      });
    }
  }

  function hidePandectesRenewButtons() {
    document.querySelectorAll('pandectes-cmp').forEach(hidePandectesRenewButton);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hidePandectesRenewButtons);
  } else {
    hidePandectesRenewButtons();
  }

  new MutationObserver(hidePandectesRenewButtons).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  [250, 750, 1500, 3000, 6000].forEach(function (delay) {
    window.setTimeout(hidePandectesRenewButtons, delay);
  });
})();

(function () {
  function normalizeProductCardWishlist(card) {
    if (!card) return;

    var wishlistHost = card.querySelector('wishlist-button-collection');
    var infoStack = card.querySelector('.product-card__info .v-stack');

    if (!wishlistHost || !infoStack) return;

    var metaRow = infoStack.querySelector('.product-card__meta-row');

    if (!metaRow) {
      metaRow = document.createElement('div');
      metaRow.className = 'product-card__meta-row';
      var categorization = infoStack.querySelector('.product-categorization');

      if (categorization) {
        categorization.parentNode.insertBefore(metaRow, categorization);
        metaRow.appendChild(categorization);
      } else {
        infoStack.insertBefore(metaRow, infoStack.firstChild);
      }
    }

    if (!metaRow.contains(wishlistHost)) {
      metaRow.appendChild(wishlistHost);
    }
  }

  function normalizeAllProductCardWishlists(root) {
    (root || document).querySelectorAll('product-card').forEach(normalizeProductCardWishlist);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      normalizeAllProductCardWishlists(document);
    });
  } else {
    normalizeAllProductCardWishlists(document);
  }

  new MutationObserver(function () {
    normalizeAllProductCardWishlists(document);
  }).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
/* End Changes 2026/05/18 (ABU) */

/* Changes 2026/07/03 (ABU) - Product-card free-shipping row alignment */
(function () {
  var containerSelector = 'product-list, .related-categories-popup__grid, .related-categories-dialog__grid';
  var directCardSelector = ':scope > product-card';
  var noteSelector = '[data-product-card-free-shipping]';
  var placeholderSelector = '[data-product-card-free-shipping-placeholder]';
  var deliverySelector = '[data-product-card-delivery]';
  var deliveryPlaceholderSelector = '[data-product-card-delivery-placeholder]';
  var deliveryLabelSelector = '[data-product-card-delivery-label]';
  var rowTopTolerance = 8;
  var scheduledSyncId = 0;

  function getContainerCards(container) {
    if (!container || typeof container.querySelectorAll !== 'function') {
      return [];
    }

    var cards = Array.from(container.querySelectorAll(directCardSelector));

    if (!cards.length && container.matches && container.matches('.related-categories-popup__grid, .related-categories-dialog__grid')) {
      cards = Array.from(container.querySelectorAll('product-card'));
    }

    return cards;
  }

  function resetCardShippingState(card) {
    var placeholder = card.querySelector(placeholderSelector);
    var deliveryPlaceholder = card.querySelector(deliveryPlaceholderSelector);

    if (placeholder) {
      placeholder.hidden = true;
    }

    if (deliveryPlaceholder) {
      deliveryPlaceholder.hidden = true;
    }
  }

  function localizeProductCardDeliveryLabel(card) {
    var label = card.querySelector(deliveryLabelSelector);

    if (!label || label.dataset.localized === 'true') {
      return;
    }

    var start = label.dataset.deliveryStart;
    var end = label.dataset.deliveryEnd || start;

    if (!start || !end) {
      return;
    }

    var startDate = new Date(start + 'T00:00:00');
    var endDate = new Date(end + 'T00:00:00');

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return;
    }

    var locale = document.documentElement.lang || 'en';
    var dateFormatter = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
    var getDatePart = function (date, type) {
      var part = dateFormatter.formatToParts(date).find(function (item) {
        return item.type === type;
      });

      return part ? part.value : '';
    };
    var startDay = getDatePart(startDate, 'day');
    var startMonth = getDatePart(startDate, 'month');
    var endDay = getDatePart(endDate, 'day');
    var endMonth = getDatePart(endDate, 'month');
    var startLabel = startDay && startMonth ? startDay + ' ' + startMonth : dateFormatter.format(startDate);
    var endLabel = endDay && endMonth ? endDay + ' ' + endMonth : dateFormatter.format(endDate);
    var dateLabel = startLabel;

    if (start !== end) {
      dateLabel = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()
        ? startDay + '-' + endDay + ' ' + endMonth
        : startLabel + '-' + endLabel;
    }

    label.textContent = label.textContent.replace(/\d{1,2}(?:-\d{1,2})?\s+[^\s-]+(?:-\d{1,2}\s+[^\s-]+)?/, dateLabel);
    label.dataset.localized = 'true';
  }

  function syncProductCardContainer(container) {
    var cards = getContainerCards(container).filter(function (card) {
      return card
        && card.getClientRects().length > 0
        && card.querySelector(noteSelector + ', ' + placeholderSelector + ', ' + deliverySelector + ', ' + deliveryPlaceholderSelector);
    });

    if (!cards.length) {
      return;
    }

    cards.forEach(function (card) {
      resetCardShippingState(card);
      localizeProductCardDeliveryLabel(card);
    });

    var containerRect = container.getBoundingClientRect();
    var positionedCards = cards.map(function (card) {
      var rect = card.getBoundingClientRect();

      return {
        card: card,
        rect: rect,
        rowTop: Math.round(rect.top - containerRect.top)
      };
    }).sort(function (leftEntry, rightEntry) {
      var topDifference = leftEntry.rowTop - rightEntry.rowTop;

      if (Math.abs(topDifference) <= rowTopTolerance) {
        return leftEntry.rect.left - rightEntry.rect.left;
      }

      return topDifference;
    });

    var rows = [];

    positionedCards.forEach(function (entry) {
      var currentRow = rows[rows.length - 1];

      if (!currentRow || Math.abs(entry.rowTop - currentRow.top) > rowTopTolerance) {
        currentRow = { top: entry.rowTop, cards: [] };
        rows.push(currentRow);
      }

      currentRow.cards.push(entry.card);
    });

    rows.forEach(function (row) {
      var rowHasFreeShipping = row.cards.some(function (card) {
        return !!card.querySelector(noteSelector);
      });

      var rowHasDelivery = row.cards.some(function (card) {
        return !!card.querySelector(deliverySelector);
      });

      if (!rowHasFreeShipping && !rowHasDelivery) {
        return;
      }

      row.cards.forEach(function (card) {
        var placeholder = card.querySelector(placeholderSelector);
        var deliveryPlaceholder = card.querySelector(deliveryPlaceholderSelector);

        if (rowHasFreeShipping && placeholder) {
          placeholder.hidden = false;
          placeholder.style.height = '';
        }

        if (rowHasDelivery && deliveryPlaceholder) {
          deliveryPlaceholder.hidden = false;
          deliveryPlaceholder.style.height = '';
        }
      });
    });
  }

  function syncAllProductCardShippingRows() {
    document.querySelectorAll(containerSelector).forEach(function (container) {
      syncProductCardContainer(container);
    });
  }

  function scheduleSync() {
    if (scheduledSyncId) {
      return;
    }

    scheduledSyncId = window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        scheduledSyncId = 0;
        syncAllProductCardShippingRows();
      });
    });
  }

  function scheduleSyncWithFollowUp() {
    scheduleSync();
    window.setTimeout(scheduleSync, 160);
    window.setTimeout(scheduleSync, 600);
  }

  window.__syncProductCardShippingRows = scheduleSyncWithFollowUp;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSyncWithFollowUp, { once: true });
  } else {
    scheduleSyncWithFollowUp();
  }

  window.addEventListener('load', scheduleSyncWithFollowUp);
  window.addEventListener('pageshow', scheduleSyncWithFollowUp);
  window.addEventListener('resize', scheduleSync);
  document.addEventListener('shopify:section:load', scheduleSyncWithFollowUp);
  document.addEventListener('facet:update', scheduleSyncWithFollowUp);
})();
/* End Changes 2026/07/03 (ABU) - Product-card free-shipping row alignment */

/* Changes 2026/06/17 (ABU) */
(function () {
  var promotionOfferSelector = '[data-member-promotion-offer]';
  /* Changes start (GLA) 30/10/2026 Fjern ending-soon logik */
  var promotionSurfaceSelector = promotionOfferSelector;
  /* Changes end (GLA) 30/10/2026 */
  var fallbackPromotionCountdownLabels = { day: 'D', hour: 'H', minute: 'M', second: 'S' };
  var promotionCountdownSegmenter = window.Intl && typeof window.Intl.Segmenter === 'function'
    ? new window.Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : null;

  function padCountdownUnit(value) {
    return String(value).padStart(2, '0');
  }

  function compactCountdownLabel(value, fallback) {
    var label = String(value || fallback || '').trim();
    if (!label) return fallback;

    if (promotionCountdownSegmenter) {
      var segments = promotionCountdownSegmenter.segment(label);
      var firstSegment = segments[Symbol.iterator]().next();
      if (!firstSegment.done) return firstSegment.value.segment;
    }

    return Array.from(label)[0] || fallback;
  }

  function getPromotionCountdownLabels(offer) {
    if (!offer) return fallbackPromotionCountdownLabels;
    if (offer.__memberPromotionCountdownLabels) return offer.__memberPromotionCountdownLabels;

    var labels = {
      day: offer.getAttribute('data-member-promotion-countdown-label-day') || '',
      hour: offer.getAttribute('data-member-promotion-countdown-label-hour') || '',
      minute: offer.getAttribute('data-member-promotion-countdown-label-minute') || '',
      second: offer.getAttribute('data-member-promotion-countdown-label-second') || ''
    };

    if (labels.day && labels.hour && labels.minute && labels.second) {
      offer.__memberPromotionCountdownLabels = {
        day: compactCountdownLabel(labels.day, fallbackPromotionCountdownLabels.day),
        hour: compactCountdownLabel(labels.hour, fallbackPromotionCountdownLabels.hour),
        minute: compactCountdownLabel(labels.minute, fallbackPromotionCountdownLabels.minute),
        second: compactCountdownLabel(labels.second, fallbackPromotionCountdownLabels.second)
      };

      return offer.__memberPromotionCountdownLabels;
    }

    offer.__memberPromotionCountdownLabels = fallbackPromotionCountdownLabels;
    return fallbackPromotionCountdownLabels;
  }

  function formatPromotionCountdown(totalSeconds, offer) {
    var currentLabels = getPromotionCountdownLabels(offer);
    var remaining = Math.max(0, totalSeconds);
    var days = Math.floor(remaining / 86400);
    var hours = Math.floor((remaining % 86400) / 3600);
    var minutes = Math.floor((remaining % 3600) / 60);
    var seconds = remaining % 60;

    if (days > 0) {
      return days + currentLabels.day + ' : ' + padCountdownUnit(hours) + currentLabels.hour + ' : ' + padCountdownUnit(minutes) + currentLabels.minute + ' : ' + padCountdownUnit(seconds) + currentLabels.second;
    }

    return padCountdownUnit(hours) + currentLabels.hour + ' : ' + padCountdownUnit(minutes) + currentLabels.minute + ' : ' + padCountdownUnit(seconds) + currentLabels.second;
  }

  function stopPromotionCountdown(offer) {
    if (offer && offer.__memberPromotionCountdownTimer) {
      window.clearInterval(offer.__memberPromotionCountdownTimer);
      offer.__memberPromotionCountdownTimer = null;
    }
  }

  function refreshPromotionSurfaceAtCountdownEnd(offer, countdownEnd) {
    if (!offer || offer.getAttribute('data-member-promotion-countdown-expiry-action') !== 'reload') return;

    var refreshKey = 'member-promotion-countdown-refreshed:' + window.location.pathname + ':' + countdownEnd;
    var refreshAttemptedAt = Date.now();

    try {
      var previousRefreshAttempt = Number(window.sessionStorage.getItem(refreshKey)) || 0;
      var refreshElapsed = refreshAttemptedAt - previousRefreshAttempt;

      if (previousRefreshAttempt > 0 && refreshElapsed >= 0 && refreshElapsed < 30000) {
        if (!offer.__memberPromotionRefreshTimer) {
          offer.__memberPromotionRefreshTimer = window.setTimeout(function () {
            offer.__memberPromotionRefreshTimer = null;
            refreshPromotionSurfaceAtCountdownEnd(offer, countdownEnd);
          }, 30000 - refreshElapsed);
        }

        return;
      }

      window.sessionStorage.setItem(refreshKey, String(refreshAttemptedAt));
      window.location.reload();
    } catch {
      offer.hidden = false;

      if (!offer.__memberPromotionRefreshTimer) {
        offer.__memberPromotionRefreshTimer = window.setTimeout(function () {
          offer.__memberPromotionRefreshTimer = null;
          window.location.reload();
        }, 30000);
      }
    }
  }

  function clearPromotionSurfaceRefreshGuard(offer, countdownEnd) {
    if (!offer || offer.getAttribute('data-member-promotion-countdown-expiry-action') !== 'reload') return;

    if (offer.__memberPromotionRefreshTimer) {
      window.clearTimeout(offer.__memberPromotionRefreshTimer);
      offer.__memberPromotionRefreshTimer = null;
    }

    try {
      window.sessionStorage.removeItem('member-promotion-countdown-refreshed:' + window.location.pathname + ':' + countdownEnd);
    } catch {
      offer.hidden = false;
    }
  }

  function updatePromotionCountdownVisibility(offer, countdownTarget, remaining) {
    var countdownContainer = countdownTarget.closest('[data-member-promotion-countdown-container]');
    if (!countdownContainer) return;

    var revealSeconds = parseInt(countdownContainer.getAttribute('data-member-promotion-countdown-reveal-seconds') || '0', 10);
    var visible = remaining > 0 && (!revealSeconds || remaining < revealSeconds);

    countdownContainer.hidden = !visible;
    offer.toggleAttribute('data-member-promotion-countdown-visible', visible);
  }

  function updatePromotionCountdown(offer) {
    if (!offer) return false;

    var countdownTarget = offer.querySelector('[data-member-promotion-countdown-target]');
    var countdownEnd = parseInt(offer.getAttribute('data-member-promotion-countdown-end') || '0', 10);
    var previewPromotion = offer.getAttribute('data-member-promotion-preview') === 'true';
    if (!countdownTarget) return false;

    if (!countdownEnd) {
      updatePromotionCountdownVisibility(offer, countdownTarget, 0);
      stopPromotionCountdown(offer);

      if (previewPromotion) {
        countdownTarget.textContent = formatPromotionCountdown(0, offer);
        offer.hidden = false;
      } else {
        countdownTarget.textContent = '';
        offer.hidden = true;
      }

      return false;
    }

    var now = Math.floor(Date.now() / 1000);
    var remaining = countdownEnd - now;

    updatePromotionCountdownVisibility(offer, countdownTarget, remaining);

    if (remaining > 0) clearPromotionSurfaceRefreshGuard(offer, countdownEnd);

    if (remaining <= 0) {
      stopPromotionCountdown(offer);

      if (previewPromotion) {
        countdownTarget.textContent = formatPromotionCountdown(0, offer);
        offer.hidden = false;
        return false;
      }

      if (offer.getAttribute('data-member-promotion-countdown-expiry-action') === 'reload') {
        countdownTarget.textContent = formatPromotionCountdown(0, offer);
        offer.hidden = false;
        refreshPromotionSurfaceAtCountdownEnd(offer, countdownEnd);
        return false;
      }

      countdownTarget.textContent = '';
      offer.hidden = true;
      return false;
    }

    countdownTarget.textContent = formatPromotionCountdown(remaining, offer);
    return true;
  }

  function initPromotionOffer(offer) {
    if (!offer || offer.dataset.memberPromotionInitialized === 'true') return;

    offer.dataset.memberPromotionInitialized = 'true';
    var shouldRunCountdown = updatePromotionCountdown(offer);

    if (offer.hidden || !shouldRunCountdown) {
      return;
    }

    if (offer.hasAttribute('data-member-promotion-countdown-end')) {
      offer.__memberPromotionCountdownTimer = window.setInterval(function () {
        if (!document.body.contains(offer)) {
          stopPromotionCountdown(offer);
          return;
        }

        if (!updatePromotionCountdown(offer)) {
          stopPromotionCountdown(offer);
        }
      }, 1000);
    }
  }

  function initPromotionOffers(root) {
    var scope = root || document;

    if (scope.matches && scope.matches(promotionOfferSelector)) {
      initPromotionOffer(scope);
    }

    if (scope.querySelectorAll) {
      scope.querySelectorAll(promotionOfferSelector).forEach(initPromotionOffer);
    }
  }

  function initializePromotionSurfaces(root) {
    /* Changes start (GLA) 30/10/2026 Fjern ending-soon logik */
    initPromotionOffers(root);
    /* Changes end (GLA) 30/10/2026 */
  }

  function fallbackCopyText(text) {
    var textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    var copied = document.execCommand('copy');
    document.body.removeChild(textarea);

    if (!copied) {
      throw new Error('Copy command failed');
    }
  }

  async function copyTextToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (error) {
      }
    }

    fallbackCopyText(text);
  }

  function setPromotionCopyButtonState(button, activeLabel, defaultLabel, state) {
    if (!button) return;

    var labelTarget = button.querySelector('[data-member-promotion-copy-label]');
    var feedbackTarget = button.querySelector('[data-member-promotion-copy-feedback]');
    var liveTarget = button.querySelector('[data-member-promotion-copy-live]');

    if (labelTarget) {
      labelTarget.textContent = activeLabel;
    } else {
      button.textContent = activeLabel;
    }

    if (feedbackTarget) {
      feedbackTarget.textContent = activeLabel;
    }

    if (liveTarget) {
      liveTarget.textContent = activeLabel;
    }

    button.setAttribute('aria-label', activeLabel);
    button.setAttribute('title', activeLabel);
    button.setAttribute('data-copy-state', state || 'default');

    if (button.__memberPromotionCopyReset) {
      window.clearTimeout(button.__memberPromotionCopyReset);
    }

    button.__memberPromotionCopyReset = window.setTimeout(function () {
      if (labelTarget) {
        labelTarget.textContent = defaultLabel;
      } else {
        button.textContent = defaultLabel;
      }

      if (feedbackTarget) {
        feedbackTarget.textContent = '';
      }

      if (liveTarget) {
        liveTarget.textContent = '';
      }

      button.setAttribute('aria-label', defaultLabel);
      button.setAttribute('title', defaultLabel);
      button.setAttribute('data-copy-state', 'default');
      button.__memberPromotionCopyReset = null;
    }, 2000);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-member-promotion-copy-button]');
    if (!button) return;

    event.preventDefault();

    var promotionCode = button.getAttribute('data-member-promotion-code') || '';
    if (!promotionCode) return;

    var defaultLabel = button.getAttribute('data-copy-label') || button.getAttribute('aria-label') || button.textContent.trim();
    var copiedLabel = button.getAttribute('data-copied-label') || 'Copied';
    var errorLabel = button.getAttribute('data-error-label') || 'Copy failed';

    copyTextToClipboard(promotionCode)
      .then(function () {
        setPromotionCopyButtonState(button, copiedLabel, defaultLabel, 'copied');
      })
      .catch(function () {
        setPromotionCopyButtonState(button, errorLabel, defaultLabel, 'error');
      });
  });

  function setSparePartsCopyButtonState(button, activeLabel, defaultLabel, state) {
    var liveTarget = button.querySelector('[data-spare-parts-copy-live]');

    button.setAttribute('aria-label', activeLabel);
    button.setAttribute('title', activeLabel);
    button.setAttribute('data-copy-state', state);

    if (liveTarget) {
      liveTarget.textContent = activeLabel;
    }

    if (button.__sparePartsCopyReset) {
      window.clearTimeout(button.__sparePartsCopyReset);
    }

    button.__sparePartsCopyReset = window.setTimeout(function () {
      button.setAttribute('aria-label', defaultLabel);
      button.setAttribute('title', defaultLabel);
      button.setAttribute('data-copy-state', 'default');

      if (liveTarget) {
        liveTarget.textContent = '';
      }

      button.__sparePartsCopyReset = null;
    }, 2000);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-spare-parts-copy-button]');
    if (!button) return;

    event.preventDefault();

    var sku = button.getAttribute('data-spare-parts-sku') || '';
    if (!sku) return;

    var defaultLabel = button.getAttribute('data-copy-label') || button.getAttribute('aria-label') || '';
    var copiedLabel = button.getAttribute('data-copied-label') || 'Copied';
    var errorLabel = button.getAttribute('data-error-label') || 'Copy failed';

    copyTextToClipboard(sku)
      .then(function () {
        setSparePartsCopyButtonState(button, copiedLabel, defaultLabel, 'copied');
      })
      .catch(function () {
        setSparePartsCopyButtonState(button, errorLabel, defaultLabel, 'error');
      });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializePromotionSurfaces(document);
    });
  } else {
    initializePromotionSurfaces(document);
  }

  /* Changes start (GLA) 30/10/2026 Fjern ending-soon logik */
  /* Changes end (GLA) 30/10/2026 */

  var promotionObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (!node || node.nodeType !== 1) return;

        var isPromotionSurface = node.matches && node.matches(promotionSurfaceSelector);
        var containsPromotionSurface = node.querySelector && node.querySelector(promotionSurfaceSelector);
        if (!isPromotionSurface && !containsPromotionSurface) return;

        initializePromotionSurfaces(node);
      });
    });
  });

  promotionObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
/* End Changes 2026/06/17 (ABU) */

/* Changes 2026/06/17 (ABU) */
(function () {
  function findFallbackQuantityInput(formId) {
    var inputs = document.querySelectorAll('[data-pdp-fallback-quantity]');

    for (var index = 0; index < inputs.length; index += 1) {
      if (inputs[index].getAttribute('form') === formId) {
        return inputs[index];
      }
    }

    return null;
  }

  function findFallbackQuantityRules(formId) {
    var rulesBlocks = document.querySelectorAll('[data-pdp-fallback-quantity-rules]');

    for (var index = 0; index < rulesBlocks.length; index += 1) {
      if (rulesBlocks[index].dataset.formId === formId) {
        try {
          return JSON.parse(rulesBlocks[index].textContent || '{}');
        } catch (error) {
          return {};
        }
      }
    }

    return {};
  }

  function getVariantMinimumQuantity(variant, formId) {
    if (!variant) {
      return 1;
    }

    var variantId = variant.id ? String(variant.id) : '';
    var rules = findFallbackQuantityRules(formId);

    if (variantId && rules[variantId] != null) {
      return rules[variantId];
    }

    if (variant.quantity_rule && variant.quantity_rule.min != null) {
      return variant.quantity_rule.min;
    }

    if (variant.quantityRule && variant.quantityRule.min != null) {
      return variant.quantityRule.min;
    }

    return 1;
  }

  function getFormIdFromVariantEvent(event) {
    var detail = event.detail || {};

    if (typeof detail.formId === 'string' && detail.formId) {
      return detail.formId;
    }

    if (detail.formId && detail.formId.form && typeof detail.formId.form.getAttribute === 'function') {
      return detail.formId.form.getAttribute('id') || '';
    }

    if (event.target && typeof event.target.getAttribute === 'function') {
      return event.target.getAttribute('id') || '';
    }

    return '';
  }

  document.addEventListener('variant:change', function (event) {
    var detail = event.detail || {};
    var formId = getFormIdFromVariantEvent(event);
    var input = formId ? findFallbackQuantityInput(formId) : null;

    if (!input) {
      return;
    }

    input.value = getVariantMinimumQuantity(detail.variant, formId);
    input.dataset.variantId = detail.variant && detail.variant.id ? String(detail.variant.id) : '';
  });
})();
/* End Changes 2026/06/17 (ABU) */

/* Changes 2026/06/17 (ABU) - PDP page-view collector */
(() => {
  const selector = 'script[type="application/json"][data-product-page-view-collector]';
  const visitorKey = "dc_pdp_view_visitor";
  const sessionKey = "dc_pdp_view_session";
  const initializedAttribute = "data-product-page-view-collector-initialized";

  const createId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }

    return `dc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  };

  const getStoredId = (storage, key) => {
    try {
      const current = storage.getItem(key);
      if (current) {
        return current;
      }

      const next = createId();
      storage.setItem(key, next);
      return next;
    } catch (error) {
      return createId();
    }
  };

  const privacyAllowsAnalytics = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== "function") {
      return true;
    }

    return privacy.analyticsProcessingAllowed();
  };

  const waitForVisibility = (config) => {
    const minimumVisibleMs = Math.max(0, Number(config.minimumVisibleMs) || 0);
    const target = config.targetSelector ? document.querySelector(config.targetSelector) : null;
    const observedElement = target || document.querySelector(".shopify-section--main-product") || document.body;

    if (!("IntersectionObserver" in window) || !observedElement) {
      return new Promise((resolve) => window.setTimeout(resolve, minimumVisibleMs));
    }

    return new Promise((resolve) => {
      let visibleTimer = 0;
      const observer = new IntersectionObserver((entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.25);

        if (!isVisible) {
          window.clearTimeout(visibleTimer);
          visibleTimer = 0;
          return;
        }

        if (visibleTimer) {
          return;
        }

        visibleTimer = window.setTimeout(() => {
          observer.disconnect();
          resolve();
        }, minimumVisibleMs);
      }, { threshold: [0, 0.25] });

      observer.observe(observedElement);
    });
  };

  const sendEvent = async (config) => {
    if (!config.endpoint || !config.productId || !privacyAllowsAnalytics()) {
      return;
    }

    const currentSessionKey = getStoredId(window.sessionStorage, sessionKey);
    const currentVisitorKey = getStoredId(window.localStorage, visitorKey);
    const source = config.source || "shopify-theme-pdp";
    const eventId = [
      source,
      config.shopDomain || config.storeDomain || window.location.hostname,
      config.productId,
      currentSessionKey
    ].join(":");

    const payload = {
      source,
      eventId,
      storeKey: config.storeKey || "",
      shopDomain: config.shopDomain || "",
      storeDomain: config.storeDomain || window.location.hostname,
      marketCode: config.marketCode || "",
      visitorCountry: config.visitorCountry || "",
      productId: config.productId,
      productHandle: config.productHandle || "",
      variantId: config.variantId || "",
      pageUrl: window.location.href,
      sessionKey: currentSessionKey,
      visitorKey: currentVisitorKey,
      occurredAt: new Date().toISOString()
    };

    try {
      await fetch(config.endpoint, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        keepalive: true,
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
    }
  };

  const initialize = () => {
    document.querySelectorAll(selector).forEach((element) => {
      if (element.hasAttribute(initializedAttribute)) {
        return;
      }

      element.setAttribute(initializedAttribute, "true");

      let config;
      try {
        config = JSON.parse(element.textContent || "{}");
      } catch (error) {
        return;
      }

      waitForVisibility(config).then(() => sendEvent(config));
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", initialize);
})();
/* End Changes 2026/06/17 (ABU) - PDP page-view collector */

/* Changes 2026/07/02 (ABU) - PDP assembly video playback */
(function () {
  var assemblyVideoBlockSelector = '.product-info__block-item[data-block-type="left-collapsible-video"]';
  var assemblyVideoExternalSelector = '[data-assembly-video-media][data-assembly-video-external]';
  var assemblyVideoNativeTemplateSelector = 'template[data-assembly-video-native-template]';
  var assemblyVideoFallbackDelayMs = 5000;
  var assemblyVideoResumeOnOpenAttribute = 'data-assembly-video-resume-on-open';
  var assemblyVideoPausedByDrawerAttribute = 'data-assembly-video-paused-by-drawer';
  var assemblyVideoFallbackCancelledAttribute = 'data-assembly-video-fallback-cancelled';

  function pauseAssemblyVideo(videoMedia, resumeOnOpen) {
    if (!videoMedia) {
      return;
    }

    var htmlVideo = videoMedia.querySelector('video');

    if (resumeOnOpen) {
      videoMedia.setAttribute(assemblyVideoPausedByDrawerAttribute, 'true');
      videoMedia.setAttribute(assemblyVideoFallbackCancelledAttribute, 'true');
    }

    videoMedia.removeAttribute('playing');

    try {
      if (videoMedia.hasAttribute('host') && videoMedia.hasAttribute('loaded')) {
        var pauseOperation = videoMedia.player.pause();
        if (pauseOperation && typeof pauseOperation.catch === 'function') {
          pauseOperation.catch(function () {
          });
        }
      } else {
        videoMedia.pause();
      }
    } catch (_) {
    }

    if (htmlVideo) {
      htmlVideo.pause();
    }
  }

  function pauseAssemblyVideoMedia(container, resumeOnOpen) {
    container.querySelectorAll('[data-assembly-video-media]').forEach(function (videoMedia) {
      pauseAssemblyVideo(videoMedia, resumeOnOpen);
    });
  }

  function resumeAssemblyVideoMedia(container) {
    container.querySelectorAll('[data-assembly-video-media]').forEach(function (videoMedia) {
      if (!videoMedia.hasAttribute(assemblyVideoResumeOnOpenAttribute)) {
        videoMedia.removeAttribute(assemblyVideoPausedByDrawerAttribute);
        return;
      }

      try {
        videoMedia.play();
      } catch (_) {
      }
    });
  }

  function isAssemblyVideoContainerOpen(videoMedia) {
    var accordion = videoMedia.closest('details');
    if (accordion && !accordion.open) {
      return false;
    }

    var drawer = videoMedia.closest('x-drawer.product-details__drawer');
    return !drawer || drawer.open;
  }

  function activateNativeAssemblyVideoFallback(videoMedia) {
    if (!videoMedia || !videoMedia.isConnected) {
      return;
    }

    var assemblyVideoFrame = videoMedia.closest('.assembly-video__frame');
    var nativeTemplate = assemblyVideoFrame ? assemblyVideoFrame.querySelector(assemblyVideoNativeTemplateSelector) : null;

    if (!nativeTemplate || !nativeTemplate.content || !nativeTemplate.content.firstElementChild) {
      return;
    }

    var nativeVideoMedia = nativeTemplate.content.firstElementChild.cloneNode(true);
    nativeTemplate.remove();
    videoMedia.replaceWith(nativeVideoMedia);

    requestAnimationFrame(function () {
      if (!nativeVideoMedia.isConnected) {
        return;
      }

      try {
        nativeVideoMedia.play();
      } catch (_) {
      }
    });
  }

  function scheduleAssemblyVideoFallback(videoMedia) {
    if (!videoMedia || !videoMedia.hasAttribute('host') || videoMedia.hasAttribute('data-assembly-video-fallback-scheduled')) {
      return;
    }

    var assemblyVideoFrame = videoMedia.closest('.assembly-video__frame');
    if (!assemblyVideoFrame || !assemblyVideoFrame.querySelector(assemblyVideoNativeTemplateSelector)) {
      return;
    }

    videoMedia.setAttribute('data-assembly-video-fallback-scheduled', 'true');

    var playbackObserver = new MutationObserver(function () {
      if (videoMedia.hasAttribute('playing')) {
        videoMedia.setAttribute('data-assembly-video-playback-started', 'true');
        playbackObserver.disconnect();
      }
    });

    playbackObserver.observe(videoMedia, {
      attributes: true,
      attributeFilter: ['playing']
    });

    window.setTimeout(function () {
      playbackObserver.disconnect();

      if (!videoMedia.isConnected) {
        return;
      }

      videoMedia.removeAttribute('data-assembly-video-fallback-scheduled');

      if (videoMedia.hasAttribute(assemblyVideoFallbackCancelledAttribute)) {
        return;
      }

      if (!isAssemblyVideoContainerOpen(videoMedia)) {
        return;
      }

      if (videoMedia.hasAttribute('playing') || videoMedia.hasAttribute('data-assembly-video-playback-started')) {
        return;
      }

      activateNativeAssemblyVideoFallback(videoMedia);
    }, assemblyVideoFallbackDelayMs);
  }

  document.addEventListener('click', function (event) {
    var target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    var externalAssemblyVideo = target.closest(assemblyVideoExternalSelector);
    if (!externalAssemblyVideo) {
      return;
    }

    externalAssemblyVideo.removeAttribute(assemblyVideoFallbackCancelledAttribute);
    externalAssemblyVideo.setAttribute(assemblyVideoResumeOnOpenAttribute, 'true');
    scheduleAssemblyVideoFallback(externalAssemblyVideo);
  }, true);

  document.addEventListener('media:play', function (event) {
    var videoMedia = event.target;

    if (videoMedia instanceof Element && videoMedia.matches('[data-assembly-video-media]')) {
      videoMedia.removeAttribute(assemblyVideoPausedByDrawerAttribute);
      videoMedia.setAttribute(assemblyVideoResumeOnOpenAttribute, 'true');
    }
  }, true);

  document.addEventListener('media:pause', function (event) {
    var videoMedia = event.target;

    if (!(videoMedia instanceof Element) || !videoMedia.matches('[data-assembly-video-media]')) {
      return;
    }

    if (!videoMedia.hasAttribute(assemblyVideoPausedByDrawerAttribute)) {
      videoMedia.removeAttribute(assemblyVideoResumeOnOpenAttribute);
    }
  }, true);

  document.addEventListener('toggle', function (event) {
    var container = event.target;

    if (!(container instanceof Element)) {
      return;
    }

    if (container.tagName === 'DETAILS') {
      if (container.open) {
        return;
      }

      var assemblyVideoBlock = container.closest(assemblyVideoBlockSelector);
      if (!assemblyVideoBlock) {
        return;
      }

      pauseAssemblyVideoMedia(assemblyVideoBlock);
      return;
    }

    if (container.matches('x-drawer.product-details__drawer') && container.querySelector('[data-assembly-video-media]')) {
      if (container.open) {
        resumeAssemblyVideoMedia(container);
      } else {
        pauseAssemblyVideoMedia(container, true);
      }
    }
  }, true);
})();
/* End Changes 2026/07/02 (ABU) - PDP assembly video playback */

/* Changes 2026/07/28 (ABU) - PDP thumbnail overflow progress */
(function () {
  class ThumbnailProgress extends HTMLElement {
    connectedCallback() {
      this.thumbnailList = document.getElementById(this.getAttribute('aria-controls'));
      this.input = this.querySelector('input[type="range"]');

      if (!this.thumbnailList || !this.input) {
        return;
      }

      this.minimumProgress = Number(this.input.min);
      this.maximumProgress = Number(this.input.max);
      this.abortController = new AbortController();
      this.input.addEventListener('input', this.onInput.bind(this), { signal: this.abortController.signal });
      this.thumbnailList.addEventListener('click', this.onThumbnailClick.bind(this), { signal: this.abortController.signal });
      this.thumbnailList.addEventListener('scroll', this.onThumbnailScroll.bind(this), { passive: true, signal: this.abortController.signal });
      this.thumbnailList.addEventListener('scrollend', this.onThumbnailScrollEnd.bind(this), { signal: this.abortController.signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.thumbnailList);
      }

      this.mutationObserver = new MutationObserver(this.onThumbnailMutations.bind(this));
      this.mutationObserver.observe(this.thumbnailList, { attributes: true, attributeFilter: ['aria-current', 'hidden'], childList: true, subtree: true });

      requestAnimationFrame(() => this.update());
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      window.clearTimeout(this.selectionScrollTimeout);
    }

    onInput(event) {
      const thumbnails = this.visibleThumbnails;

      if (thumbnails.length < 2) {
        return;
      }

      const position = (Number(event.target.value) - this.minimumProgress) / (this.maximumProgress - this.minimumProgress);
      const targetIndex = Math.round(position * (thumbnails.length - 1));
      thumbnails[targetIndex]?.click();
    }

    onThumbnailClick() {
      this.onThumbnailSelectionChange();
    }

    onThumbnailMutations(mutations) {
      if (mutations.some((mutation) => mutation.type === 'attributes' && mutation.attributeName === 'aria-current')) {
        this.onThumbnailSelectionChange();
        return;
      }

      this.update();
    }

    onThumbnailSelectionChange() {
      this.isFollowingSelection = true;
      this.scheduleSelectionScrollEnd();
      requestAnimationFrame(() => this.update());
    }

    onThumbnailScroll() {
      if (this.isFollowingSelection) {
        this.scheduleSelectionScrollEnd();
        return;
      }

      requestAnimationFrame(() => this.update(true));
    }

    onThumbnailScrollEnd() {
      if (!this.isFollowingSelection) {
        return;
      }

      window.clearTimeout(this.selectionScrollTimeout);
      this.isFollowingSelection = false;
      this.update();
    }

    scheduleSelectionScrollEnd() {
      window.clearTimeout(this.selectionScrollTimeout);
      this.selectionScrollTimeout = window.setTimeout(() => this.onThumbnailScrollEnd(), 100);
    }

    get maximumScroll() {
      return Math.max(this.thumbnailList.scrollWidth - this.thumbnailList.clientWidth, 0);
    }

    get visibleThumbnails() {
      return Array.from(this.thumbnailList.querySelectorAll('.product-gallery__thumbnail:not([hidden])'));
    }

    update(preferScrollPosition = false) {
      const maximumScroll = this.maximumScroll;
      const hasOverflow = maximumScroll > 1;
      const thumbnails = this.visibleThumbnails;
      const selectedIndex = thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-current') === 'true');
      const selectedProgress = thumbnails.length > 1 && selectedIndex >= 0
        ? this.minimumProgress + (selectedIndex / (thumbnails.length - 1)) * (this.maximumProgress - this.minimumProgress)
        : this.minimumProgress;
      const scrollProgress = hasOverflow
        ? this.minimumProgress + (Math.max(0, Math.min(this.thumbnailList.scrollLeft, maximumScroll)) / maximumScroll) * (this.maximumProgress - this.minimumProgress)
        : this.minimumProgress;
      const progress = preferScrollPosition ? scrollProgress : selectedProgress;

      this.toggleAttribute('data-overflow', hasOverflow);
      this.input.disabled = !hasOverflow;
      this.input.value = String(Math.round(progress));
      this.input.style.setProperty('--thumbnail-progress', `${progress}%`);
    }
  }

  if (!window.customElements.get('thumbnail-progress')) {
    window.customElements.define('thumbnail-progress', ThumbnailProgress);
  }
})();
/* End Changes 2026/07/28 (ABU) - PDP thumbnail overflow progress */
